import React from "react";
import { getBlankPattern } from "./PresetPatterns";

// General Utility Methods ---------------------------------

/**
 * Compares two objects to find if they are equal
 *
 * @param {Object || Array} object1
 * @param {Object || Array} object2
 * @returns {Boolean}
 */
export function areObjectsEqual(obj1, obj2) {
  const object1 = { ...obj1 };
  const object2 = { ...obj2 };
  if (Array.isArray(object1) && Array.isArray(object2)) {
    return object1.sort().toString() === object2.sort().toString();
  } else if (typeof object1 === "object" && typeof object2 === "object") {
    const object1Keys = Object.keys(object1);
    const object2Keys = Object.keys(object2);

    if (object1Keys.length !== object2Keys.length) {
      return false;
    }

    for (let objectKey of object1Keys) {
      if (object1[objectKey] !== object2[objectKey]) {
        if (typeof object1[objectKey] == "object" && typeof object2[objectKey] == "object") {
          if (!areObjectsEqual(object1[objectKey], object2[objectKey])) {
            return false;
          }
        } else {
          return false;
        }
      }
    }

    return true;
  }
}

/**
 * Dispratches a custom event on the document element
 *
 * @param   {String}  eventName  name of custom event
 * @param   {Mixed}  detail     detail value passed with the event
 */
export function dispatchCustomEvent(eventName, detail) {
  document.dispatchEvent(new CustomEvent(eventName, { detail: detail }));
}

/**
 * Exports the default settings for the Settings.js component
 *
 * @return  {Object}  Default settings
 */
export function getDefaultSettings() {
  return {
    audibleCaller: false,
    audibleChime: false,
    automaticCalling: false,
    caller: "",
    callerMp3: "",
    callLetters: true,
    cardDisplayColor: "",
    chattyCaller: false,
    chime: "",
    classicSideControls: true,
    coloredRows: false,
    delay: 30,
    disableMainScreenPattern: false,
    disableMainScreenPatternWhileInPlay: true,
    doubleCall: false,
    evenOdds: false,
    fullscreen: false,
    gameNumber: "",
    hideControls: false,
    hideFooter: false,
    hideUnusedNumbers: false,
    hideShuffle: false,
    hotBall: false,
    hotBallNumber: null,
    hotBallPrize: "",
    hotBallTitle: "Jackpot",
    infoCalls: true,
    infoGameNumber: false,
    infoPrevious: true,
    infoCurrent: false,
    keyboardControls: false,
    layout: "classic",
    manual: false,
    mp3Caller: false,
    numberOfCallsBeforePause: 0,
    pauseAfterXCalls: false,
    pattern: { ...getBlankPattern() },
    patternShowTitle: true,
    popOutControls: false,
    previousCallsToShow: 5,
    prize: "",
    settingsPanelOpen: false,
    showCountdown: false,
    showCurrentCall: true,
    showLetters: true,
    showMainScreenPatternSelect: false,
    showMainScreenRotatingPatterns: true,
    showPreviousCalls: true,
    skipUnusedNumbers: false,
    skipUnusedIndicator: false,
    stoplight: false,
    theme: "lpb",
    use90Balls: false,
    wildBingo: false,
    wildBingoDouble: false,
    wildBingoEvensAndOdds: false,
    wildBingoEvens: false,
    wildBingoOdds: false,
    wildBingoCustom: false,
    wildBingoCustomValue: 1,
  };
}

/**
 * Generates a random number between 2 values
 * Rejects if it is included in the called values
 *
 * @param   {Integer}  min              Floor of possible numbers
 * @param   {Integer}  max              Ceiling of possible numbers
 * @param   {Array}    calledObjects    Array of called ball objects
 *
 * @return  {Mixed}                     Could be string or generated number
 */
export function getRandomNumberInRange(min, max, calledObjects = []) {
  // convert called to a list of just values
  let called;
  if (calledObjects.length > 0 && typeof calledObjects[0] === "number") {
    called = calledObjects;
  } else {
    called = calledObjects.map((calledObject) => calledObject.number);
  }
  // if the called length is smaller than the max number
  // go ahead and select the next number, otherwise return message
  if (called.length < max) {
    for (let count = 0; count < 5000; count++) {
      let array = new Uint8Array(1);
      // only allow this to run 5k times
      window.crypto.getRandomValues(array);
      let value = array[0] % (max + 1);
      if (!called.includes(value) && value >= min && value <= max) {
        return value;
      }
    }
    throw new Error("unique random not found");
  }
  throw new Error("max has been reached");
}

/**
 * Generates a random value from array
 *
 * @param   {Array}    array    Array of potential values
 *
 * @return  {Object}   object with new array and selected value
 */
export function pullRandomValueFromArray(array) {
  const arrayCopy = [...array];
  if (arrayCopy.length === 0 || arrayCopy.every((val) => val === null)) {
    return { selectedValue: null, array: arrayCopy };
  }

  // Use crypto.getRandomValues to get a cryptographically secure random index
  let selectedValue = null;

  for (let count = 0; count < 5000; count++) {
    // Generate a random index
    const randomBuffer = new Uint32Array(1);
    window.crypto.getRandomValues(randomBuffer);
    const randomIndex = randomBuffer[0] % arrayCopy.length;

    // Get the value at that index
    const value = arrayCopy[randomIndex];

    // If the value is not null, we've found our selection
    if (value !== null) {
      selectedValue = value;
      arrayCopy[randomIndex] = null;
      break;
    }
  }
  if (selectedValue !== null) {
    return { selectedValue, array: arrayCopy };
  }

  throw new Error("Could not select a non-null value from array");
}

/**
 * Returns a string value representing the size of the user's window
 * @returns String
 */
export function getScreenSize() {
  if (window.matchMedia("(min-width: 1537px)").matches) return "xx-large"; // 1920x1080
  if (window.matchMedia("(min-width: 1441px)").matches) return "x-large"; // 1536x864
  if (window.matchMedia("(min-width: 1281px)").matches) return "large"; // 1440x900
  if (window.matchMedia("(min-width: 811px)").matches) return "medium"; // 1280x720/800
  if (window.matchMedia("(min-width: 535px)").matches) return "small"; // tablets 810x1080
  if (window.matchMedia("(min-width: 391px)").matches) return "x-small"; // large phones 430x932
  return "mobile"; // small phones 390-393
}

/**
 * Used to update the settings when a user hasn't played in
 * a while and doesn't have settings for new features.
 * @param {Object} settings
 * @returns {Object} settings
 */
export function reconcileSettings(settings) {
  const defaults = getDefaultSettings();

  // Only carry over keys that still exist in defaults (strips stale/removed keys)
  const reconciled = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (key in settings && settings[key] !== undefined && settings[key] !== null) {
      reconciled[key] = settings[key];
    }
  }

  // Validate layout — a bad/stale value here causes a blank screen
  const validLayouts = ["classic", "classic-reverse", "easy-view", "legacy-edition", "ninety-ball", "simple", "stacked", "vertical"];
  if (!validLayouts.includes(reconciled.layout)) {
    reconciled.layout = defaults.layout;
  }

  // Validate pattern — must be an object with a value property, otherwise reset to default
  if (!reconciled.pattern || typeof reconciled.pattern !== "object" || !reconciled.pattern.value) {
    reconciled.pattern = defaults.pattern;
  }

  return reconciled;
}

/**
 * Toggles full screen view on the application
 *
 * @param   {Boolean}  state  represents if full screen should be active or not
 */
export function toggleFullScreen(state) {
  let elem = document.documentElement;
  if (state === true) {
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      /* Safari */
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      /* IE11 */
      elem.msRequestFullscreen();
    }
  } else {
    if (document.fullscreenElement !== null) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        /* Safari */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        /* IE11 */
        document.msExitFullscreen();
      }
    }
  }
}

/**
 * Converts a string to capitalized string
 * Used on the pattern submission page to capitalize entries
 * @param {String} string
 * @returns String
 */
export function toProperCase(string) {
  return string.replace(/\b\w+/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

// Bingo Board Methods -------------------------------------

/**
 * Generates the needed bingo balls
 *
 * @var  {Object}
 */
export function generateBingoBoard(use90Balls = false) {
  let board = {};
  let letters = ["B", "I", "N", "G", "O"];
  let count = 1;
  letters.forEach((letter) => {
    board[letter] = [];
    for (let i = 1; i <= (use90Balls ? 18 : 15); i++) {
      let obj = {
        letter: letter,
        color: getBallColor(letter),
        number: count,
        display: letter + count,
        hide: false,
        called: false,
        active: false,
      };
      board[letter].push(obj);
      count++;
    }
  });

  return board;
}

// Bingo Card Methods --------------------------------------

/**
 * Generates a bingo card
 * @returns {Object}
 */
export function generateBingoCard() {
  const columns = ["B", "I", "N", "G", "O"];
  let card = {};
  columns.map((letter, index) => {
    let chosenNumbers = [];
    let min = index === 0 ? 1 : index * 5 * 3 + 1;
    let max = (index + 1) * 3 * 5;
    for (let i = 0; i < 5; i++) {
      chosenNumbers.push(getRandomNumberInRange(min, max, chosenNumbers));
    }
    card[letter] = chosenNumbers;
    return letter;
  });
  return card;
}

/**
 * Generates a bingo strip
 * @returns {Object}
 */
export function generateBingoStrip() {
  // A strip contains 6 tickets, each ticket has 3 rows and 9 columns
  // Each row has exactly 5 numbers and 4 blanks
  // All 90 numbers (1-90) appear exactly once across the 6 tickets
  // Numbers are placed in columns by their value:
  // Col 0: 1-9, Col 1: 10-19, Col 2: 20-29, ..., Col 8: 80-90

  // Step 1: Generate all 90 numbers organized by column
  const allColumnNumbers = {
    0: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    1: [10, 11, 12, 13, 14, 15, 16, 17, 18, 19],
    2: [20, 21, 22, 23, 24, 25, 26, 27, 28, 29],
    3: [30, 31, 32, 33, 34, 35, 36, 37, 38, 39],
    4: [40, 41, 42, 43, 44, 45, 46, 47, 48, 49],
    5: [50, 51, 52, 53, 54, 55, 56, 57, 58, 59],
    6: [60, 61, 62, 63, 64, 65, 66, 67, 68, 69],
    7: [70, 71, 72, 73, 74, 75, 76, 77, 78, 79],
    8: [80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90],
  };

  // Create a deck of 90 "column assignment cards" to deal
  const columnCounts = [9, 10, 10, 10, 10, 10, 10, 10, 11];
  const deck = [];
  for (let col = 0; col < 9; col++) {
    for (let i = 0; i < columnCounts[col]; i++) {
      deck.push(col);
    }
  }

  // Shuffle the deck thoroughly
  shuffleArray(deck);

  // Deal exactly 5 cards to each row (90 cards / 18 rows = 5 cards per row)
  const rowColumnAssignments = [];
  for (let rowIndex = 0; rowIndex < 18; rowIndex++) {
    const cardsForThisRow = deck.splice(0, 5); // Take 5 cards
    rowColumnAssignments.push(cardsForThisRow);
  }

  // Now fix any rows that have duplicate columns
  // We'll swap duplicates with cards from other rows
  for (let rowIndex = 0; rowIndex < 18; rowIndex++) {
    const row = rowColumnAssignments[rowIndex];
    const uniqueCols = new Set(row);

    // If this row has duplicates, fix it
    while (uniqueCols.size < 5) {
      // Find the duplicate column
      const colCounts = {};
      row.forEach((col) => {
        colCounts[col] = (colCounts[col] || 0) + 1;
      });

      const duplicateCol = Object.keys(colCounts).find((col) => colCounts[col] > 1);
      const duplicateIndex = row.lastIndexOf(parseInt(duplicateCol));

      // Find another row that has a column we need and doesn't have this duplicate
      let swapSuccess = false;
      for (let otherRowIndex = 0; otherRowIndex < 18; otherRowIndex++) {
        if (otherRowIndex === rowIndex) continue;

        const otherRow = rowColumnAssignments[otherRowIndex];
        const otherRowSet = new Set(otherRow);

        // Look for a column in otherRow that we don't have
        for (let otherColIndex = 0; otherColIndex < 5; otherColIndex++) {
          const otherCol = otherRow[otherColIndex];

          // Check if:
          // 1. We don't have this column yet
          // 2. The other row doesn't already have our duplicate column (to avoid creating a duplicate there)
          if (!uniqueCols.has(otherCol) && !otherRowSet.has(parseInt(duplicateCol))) {
            // Swap the duplicate with this column
            row[duplicateIndex] = otherCol;
            otherRow[otherColIndex] = parseInt(duplicateCol);

            uniqueCols.clear();
            row.forEach((col) => uniqueCols.add(col));

            swapSuccess = true;
            break;
          }
        }

        if (swapSuccess) break;
      }

      if (!swapSuccess) {
        throw new Error(`Could not fix duplicates in row ${rowIndex}`);
      }
    }

    // Sort the columns for this row
    row.sort((a, b) => a - b);
  }

  // Count how many times each column was assigned
  const assignmentCounts = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  rowColumnAssignments.forEach((row) => {
    row.forEach((col) => assignmentCounts[col]++);
  });

  // Step 2: Pre-assign which number goes in which row/column
  // Create a 18x9 grid to hold number assignments for all rows
  const numberAssignments = Array(18)
    .fill(null)
    .map(() => Array(9).fill(null));

  // For each column, distribute its numbers to all rows that need that column
  for (let col = 0; col < 9; col++) {
    // Find all row positions that need this column (counting duplicates if any)
    const rowPositions = [];
    for (let rowIndex = 0; rowIndex < 18; rowIndex++) {
      // Count how many times this column appears in this row
      const countInRow = rowColumnAssignments[rowIndex].filter((c) => c === col).length;
      for (let j = 0; j < countInRow; j++) {
        rowPositions.push(rowIndex);
      }
    }

    // Shuffle the numbers for this column
    shuffleArray(allColumnNumbers[col]);

    // Assign numbers to rows in order (this ensures each row gets a real number)
    for (let i = 0; i < rowPositions.length; i++) {
      const rowIndex = rowPositions[i];
      const number = allColumnNumbers[col][i];
      numberAssignments[rowIndex][col] = number;
    }
  }

  // Step 3: Build the 6 tickets from numberAssignments
  const strip = [];
  for (let ticketIndex = 0; ticketIndex < 6; ticketIndex++) {
    const ticket = { rows: [Array(9).fill(null), Array(9).fill(null), Array(9).fill(null)] };

    // Fill in the 3 rows for this ticket
    for (let rowInTicket = 0; rowInTicket < 3; rowInTicket++) {
      const globalRowIndex = ticketIndex * 3 + rowInTicket;
      ticket.rows[rowInTicket] = numberAssignments[globalRowIndex];
    }

    strip.push(ticket);
  }

  return strip;
}

export function shuffleArray(array) {
  let currentIndex = array.length,
    randomIndex;

  // While there remain elements to shuffle.
  while (currentIndex !== 0) {
    // Pick a remaining element.
    randomIndex = getRandomNumberInRange(0, array.length - 1);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
  }

  return array;
}

// Audio Methods -------------------------------------------
export function getAudibleCallers() {
  const lea = {
    0: "/assets/audio/lea/0.mp3",
    1: "/assets/audio/lea/1.mp3",
    2: "/assets/audio/lea/2.mp3",
    3: "/assets/audio/lea/3.mp3",
    4: "/assets/audio/lea/4.mp3",
    5: "/assets/audio/lea/5.mp3",
    6: "/assets/audio/lea/6.mp3",
    7: "/assets/audio/lea/7.mp3",
    8: "/assets/audio/lea/8.mp3",
    9: "/assets/audio/lea/9.mp3",
    10: "/assets/audio/lea/10.mp3",
    11: "/assets/audio/lea/11.mp3",
    12: "/assets/audio/lea/12.mp3",
    13: "/assets/audio/lea/13.mp3",
    14: "/assets/audio/lea/14.mp3",
    15: "/assets/audio/lea/15.mp3",
    16: "/assets/audio/lea/16.mp3",
    17: "/assets/audio/lea/17.mp3",
    18: "/assets/audio/lea/18.mp3",
    19: "/assets/audio/lea/19.mp3",
    20: "/assets/audio/lea/20.mp3",
    21: "/assets/audio/lea/21.mp3",
    22: "/assets/audio/lea/22.mp3",
    23: "/assets/audio/lea/23.mp3",
    24: "/assets/audio/lea/24.mp3",
    25: "/assets/audio/lea/25.mp3",
    26: "/assets/audio/lea/26.mp3",
    27: "/assets/audio/lea/27.mp3",
    28: "/assets/audio/lea/28.mp3",
    29: "/assets/audio/lea/29.mp3",
    30: "/assets/audio/lea/30.mp3",
    31: "/assets/audio/lea/31.mp3",
    32: "/assets/audio/lea/32.mp3",
    33: "/assets/audio/lea/33.mp3",
    34: "/assets/audio/lea/34.mp3",
    35: "/assets/audio/lea/35.mp3",
    36: "/assets/audio/lea/36.mp3",
    37: "/assets/audio/lea/37.mp3",
    38: "/assets/audio/lea/38.mp3",
    39: "/assets/audio/lea/39.mp3",
    40: "/assets/audio/lea/40.mp3",
    41: "/assets/audio/lea/41.mp3",
    42: "/assets/audio/lea/42.mp3",
    43: "/assets/audio/lea/43.mp3",
    44: "/assets/audio/lea/44.mp3",
    45: "/assets/audio/lea/45.mp3",
    46: "/assets/audio/lea/46.mp3",
    47: "/assets/audio/lea/47.mp3",
    48: "/assets/audio/lea/48.mp3",
    49: "/assets/audio/lea/49.mp3",
    50: "/assets/audio/lea/50.mp3",
    51: "/assets/audio/lea/51.mp3",
    52: "/assets/audio/lea/52.mp3",
    53: "/assets/audio/lea/53.mp3",
    54: "/assets/audio/lea/54.mp3",
    55: "/assets/audio/lea/55.mp3",
    56: "/assets/audio/lea/56.mp3",
    57: "/assets/audio/lea/57.mp3",
    58: "/assets/audio/lea/58.mp3",
    59: "/assets/audio/lea/59.mp3",
    60: "/assets/audio/lea/60.mp3",
    61: "/assets/audio/lea/61.mp3",
    62: "/assets/audio/lea/62.mp3",
    63: "/assets/audio/lea/63.mp3",
    64: "/assets/audio/lea/64.mp3",
    65: "/assets/audio/lea/65.mp3",
    66: "/assets/audio/lea/66.mp3",
    67: "/assets/audio/lea/67.mp3",
    68: "/assets/audio/lea/68.mp3",
    69: "/assets/audio/lea/69.mp3",
    70: "/assets/audio/lea/70.mp3",
    71: "/assets/audio/lea/71.mp3",
    72: "/assets/audio/lea/72.mp3",
    73: "/assets/audio/lea/73.mp3",
    74: "/assets/audio/lea/74.mp3",
    75: "/assets/audio/lea/75.mp3",
    hotball: "/assets/audio/lea/hotball.mp3",
    byitself: "/assets/audio/lea/byitself.mp3",
    letsplaybingo: "/assets/audio/lea/letsplaybingo.mp3",
    b: "/assets/audio/lea/b.mp3",
    i: "/assets/audio/lea/i.mp3",
    n: "/assets/audio/lea/n.mp3",
    g: "/assets/audio/lea/g.mp3",
    o: "/assets/audio/lea/o.mp3",
    underthe: "/assets/audio/lea/underthe.mp3",
  };

  const mathieu = {
    0: "/assets/audio/mathieu/0.mp3",
    1: "/assets/audio/mathieu/1.mp3",
    2: "/assets/audio/mathieu/2.mp3",
    3: "/assets/audio/mathieu/3.mp3",
    4: "/assets/audio/mathieu/4.mp3",
    5: "/assets/audio/mathieu/5.mp3",
    6: "/assets/audio/mathieu/6.mp3",
    7: "/assets/audio/mathieu/7.mp3",
    8: "/assets/audio/mathieu/8.mp3",
    9: "/assets/audio/mathieu/9.mp3",
    10: "/assets/audio/mathieu/10.mp3",
    11: "/assets/audio/mathieu/11.mp3",
    12: "/assets/audio/mathieu/12.mp3",
    13: "/assets/audio/mathieu/13.mp3",
    14: "/assets/audio/mathieu/14.mp3",
    15: "/assets/audio/mathieu/15.mp3",
    16: "/assets/audio/mathieu/16.mp3",
    17: "/assets/audio/mathieu/17.mp3",
    18: "/assets/audio/mathieu/18.mp3",
    19: "/assets/audio/mathieu/19.mp3",
    20: "/assets/audio/mathieu/20.mp3",
    21: "/assets/audio/mathieu/21.mp3",
    22: "/assets/audio/mathieu/22.mp3",
    23: "/assets/audio/mathieu/23.mp3",
    24: "/assets/audio/mathieu/24.mp3",
    25: "/assets/audio/mathieu/25.mp3",
    26: "/assets/audio/mathieu/26.mp3",
    27: "/assets/audio/mathieu/27.mp3",
    28: "/assets/audio/mathieu/28.mp3",
    29: "/assets/audio/mathieu/29.mp3",
    30: "/assets/audio/mathieu/30.mp3",
    31: "/assets/audio/mathieu/31.mp3",
    32: "/assets/audio/mathieu/32.mp3",
    33: "/assets/audio/mathieu/33.mp3",
    34: "/assets/audio/mathieu/34.mp3",
    35: "/assets/audio/mathieu/35.mp3",
    36: "/assets/audio/mathieu/36.mp3",
    37: "/assets/audio/mathieu/37.mp3",
    38: "/assets/audio/mathieu/38.mp3",
    39: "/assets/audio/mathieu/39.mp3",
    40: "/assets/audio/mathieu/40.mp3",
    41: "/assets/audio/mathieu/41.mp3",
    42: "/assets/audio/mathieu/42.mp3",
    43: "/assets/audio/mathieu/43.mp3",
    44: "/assets/audio/mathieu/44.mp3",
    45: "/assets/audio/mathieu/45.mp3",
    46: "/assets/audio/mathieu/46.mp3",
    47: "/assets/audio/mathieu/47.mp3",
    48: "/assets/audio/mathieu/48.mp3",
    49: "/assets/audio/mathieu/49.mp3",
    50: "/assets/audio/mathieu/50.mp3",
    51: "/assets/audio/mathieu/51.mp3",
    52: "/assets/audio/mathieu/52.mp3",
    53: "/assets/audio/mathieu/53.mp3",
    54: "/assets/audio/mathieu/54.mp3",
    55: "/assets/audio/mathieu/55.mp3",
    56: "/assets/audio/mathieu/56.mp3",
    57: "/assets/audio/mathieu/57.mp3",
    58: "/assets/audio/mathieu/58.mp3",
    59: "/assets/audio/mathieu/59.mp3",
    60: "/assets/audio/mathieu/60.mp3",
    61: "/assets/audio/mathieu/61.mp3",
    62: "/assets/audio/mathieu/62.mp3",
    63: "/assets/audio/mathieu/63.mp3",
    64: "/assets/audio/mathieu/64.mp3",
    65: "/assets/audio/mathieu/65.mp3",
    66: "/assets/audio/mathieu/66.mp3",
    67: "/assets/audio/mathieu/67.mp3",
    68: "/assets/audio/mathieu/68.mp3",
    69: "/assets/audio/mathieu/69.mp3",
    70: "/assets/audio/mathieu/70.mp3",
    71: "/assets/audio/mathieu/71.mp3",
    72: "/assets/audio/mathieu/72.mp3",
    73: "/assets/audio/mathieu/73.mp3",
    74: "/assets/audio/mathieu/74.mp3",
    75: "/assets/audio/mathieu/75.mp3",
    hotball: "/assets/audio/mathieu/hotball.mp3",
    byitself: "/assets/audio/mathieu/byitself.mp3",
    letsplaybingo: "/assets/audio/mathieu/letsplaybingo.mp3",
    b: "/assets/audio/mathieu/b.mp3",
    i: "/assets/audio/mathieu/i.mp3",
    n: "/assets/audio/mathieu/n.mp3",
    g: "/assets/audio/mathieu/g.mp3",
    o: "/assets/audio/mathieu/o.mp3",
    underthe: "/assets/audio/mathieu/underthe.mp3",
  };

  const matthew = {
    0: "/assets/audio/matthew/0.mp3",
    1: "/assets/audio/matthew/1.mp3",
    2: "/assets/audio/matthew/2.mp3",
    3: "/assets/audio/matthew/3.mp3",
    4: "/assets/audio/matthew/4.mp3",
    5: "/assets/audio/matthew/5.mp3",
    6: "/assets/audio/matthew/6.mp3",
    7: "/assets/audio/matthew/7.mp3",
    8: "/assets/audio/matthew/8.mp3",
    9: "/assets/audio/matthew/9.mp3",
    10: "/assets/audio/matthew/10.mp3",
    11: "/assets/audio/matthew/11.mp3",
    12: "/assets/audio/matthew/12.mp3",
    13: "/assets/audio/matthew/13.mp3",
    14: "/assets/audio/matthew/14.mp3",
    15: "/assets/audio/matthew/15.mp3",
    16: "/assets/audio/matthew/16.mp3",
    17: "/assets/audio/matthew/17.mp3",
    18: "/assets/audio/matthew/18.mp3",
    19: "/assets/audio/matthew/19.mp3",
    20: "/assets/audio/matthew/20.mp3",
    21: "/assets/audio/matthew/21.mp3",
    22: "/assets/audio/matthew/22.mp3",
    23: "/assets/audio/matthew/23.mp3",
    24: "/assets/audio/matthew/24.mp3",
    25: "/assets/audio/matthew/25.mp3",
    26: "/assets/audio/matthew/26.mp3",
    27: "/assets/audio/matthew/27.mp3",
    28: "/assets/audio/matthew/28.mp3",
    29: "/assets/audio/matthew/29.mp3",
    30: "/assets/audio/matthew/30.mp3",
    31: "/assets/audio/matthew/31.mp3",
    32: "/assets/audio/matthew/32.mp3",
    33: "/assets/audio/matthew/33.mp3",
    34: "/assets/audio/matthew/34.mp3",
    35: "/assets/audio/matthew/35.mp3",
    36: "/assets/audio/matthew/36.mp3",
    37: "/assets/audio/matthew/37.mp3",
    38: "/assets/audio/matthew/38.mp3",
    39: "/assets/audio/matthew/39.mp3",
    40: "/assets/audio/matthew/40.mp3",
    41: "/assets/audio/matthew/41.mp3",
    42: "/assets/audio/matthew/42.mp3",
    43: "/assets/audio/matthew/43.mp3",
    44: "/assets/audio/matthew/44.mp3",
    45: "/assets/audio/matthew/45.mp3",
    46: "/assets/audio/matthew/46.mp3",
    47: "/assets/audio/matthew/47.mp3",
    48: "/assets/audio/matthew/48.mp3",
    49: "/assets/audio/matthew/49.mp3",
    50: "/assets/audio/matthew/50.mp3",
    51: "/assets/audio/matthew/51.mp3",
    52: "/assets/audio/matthew/52.mp3",
    53: "/assets/audio/matthew/53.mp3",
    54: "/assets/audio/matthew/54.mp3",
    55: "/assets/audio/matthew/55.mp3",
    56: "/assets/audio/matthew/56.mp3",
    57: "/assets/audio/matthew/57.mp3",
    58: "/assets/audio/matthew/58.mp3",
    59: "/assets/audio/matthew/59.mp3",
    60: "/assets/audio/matthew/60.mp3",
    61: "/assets/audio/matthew/61.mp3",
    62: "/assets/audio/matthew/62.mp3",
    63: "/assets/audio/matthew/63.mp3",
    64: "/assets/audio/matthew/64.mp3",
    65: "/assets/audio/matthew/65.mp3",
    66: "/assets/audio/matthew/66.mp3",
    67: "/assets/audio/matthew/67.mp3",
    68: "/assets/audio/matthew/68.mp3",
    69: "/assets/audio/matthew/69.mp3",
    70: "/assets/audio/matthew/70.mp3",
    71: "/assets/audio/matthew/71.mp3",
    72: "/assets/audio/matthew/72.mp3",
    73: "/assets/audio/matthew/73.mp3",
    74: "/assets/audio/matthew/74.mp3",
    75: "/assets/audio/matthew/75.mp3",
    hotball: "/assets/audio/matthew/hotball.mp3",
    byitself: "/assets/audio/matthew/byitself.mp3",
    letsplaybingo: "/assets/audio/matthew/letsplaybingo.mp3",
    b: "/assets/audio/matthew/b.mp3",
    i: "/assets/audio/matthew/i.mp3",
    n: "/assets/audio/matthew/n.mp3",
    g: "/assets/audio/matthew/g.mp3",
    o: "/assets/audio/matthew/o.mp3",
    underthe: "/assets/audio/matthew/underthe.mp3",
  };

  const miguel = {
    0: "/assets/audio/miguel/0.mp3",
    1: "/assets/audio/miguel/1.mp3",
    2: "/assets/audio/miguel/2.mp3",
    3: "/assets/audio/miguel/3.mp3",
    4: "/assets/audio/miguel/4.mp3",
    5: "/assets/audio/miguel/5.mp3",
    6: "/assets/audio/miguel/6.mp3",
    7: "/assets/audio/miguel/7.mp3",
    8: "/assets/audio/miguel/8.mp3",
    9: "/assets/audio/miguel/9.mp3",
    10: "/assets/audio/miguel/10.mp3",
    11: "/assets/audio/miguel/11.mp3",
    12: "/assets/audio/miguel/12.mp3",
    13: "/assets/audio/miguel/13.mp3",
    14: "/assets/audio/miguel/14.mp3",
    15: "/assets/audio/miguel/15.mp3",
    16: "/assets/audio/miguel/16.mp3",
    17: "/assets/audio/miguel/17.mp3",
    18: "/assets/audio/miguel/18.mp3",
    19: "/assets/audio/miguel/19.mp3",
    20: "/assets/audio/miguel/20.mp3",
    21: "/assets/audio/miguel/21.mp3",
    22: "/assets/audio/miguel/22.mp3",
    23: "/assets/audio/miguel/23.mp3",
    24: "/assets/audio/miguel/24.mp3",
    25: "/assets/audio/miguel/25.mp3",
    26: "/assets/audio/miguel/26.mp3",
    27: "/assets/audio/miguel/27.mp3",
    28: "/assets/audio/miguel/28.mp3",
    29: "/assets/audio/miguel/29.mp3",
    30: "/assets/audio/miguel/30.mp3",
    31: "/assets/audio/miguel/31.mp3",
    32: "/assets/audio/miguel/32.mp3",
    33: "/assets/audio/miguel/33.mp3",
    34: "/assets/audio/miguel/34.mp3",
    35: "/assets/audio/miguel/35.mp3",
    36: "/assets/audio/miguel/36.mp3",
    37: "/assets/audio/miguel/37.mp3",
    38: "/assets/audio/miguel/38.mp3",
    39: "/assets/audio/miguel/39.mp3",
    40: "/assets/audio/miguel/40.mp3",
    41: "/assets/audio/miguel/41.mp3",
    42: "/assets/audio/miguel/42.mp3",
    43: "/assets/audio/miguel/43.mp3",
    44: "/assets/audio/miguel/44.mp3",
    45: "/assets/audio/miguel/45.mp3",
    46: "/assets/audio/miguel/46.mp3",
    47: "/assets/audio/miguel/47.mp3",
    48: "/assets/audio/miguel/48.mp3",
    49: "/assets/audio/miguel/49.mp3",
    50: "/assets/audio/miguel/50.mp3",
    51: "/assets/audio/miguel/51.mp3",
    52: "/assets/audio/miguel/52.mp3",
    53: "/assets/audio/miguel/53.mp3",
    54: "/assets/audio/miguel/54.mp3",
    55: "/assets/audio/miguel/55.mp3",
    56: "/assets/audio/miguel/56.mp3",
    57: "/assets/audio/miguel/57.mp3",
    58: "/assets/audio/miguel/58.mp3",
    59: "/assets/audio/miguel/59.mp3",
    60: "/assets/audio/miguel/60.mp3",
    61: "/assets/audio/miguel/61.mp3",
    62: "/assets/audio/miguel/62.mp3",
    63: "/assets/audio/miguel/63.mp3",
    64: "/assets/audio/miguel/64.mp3",
    65: "/assets/audio/miguel/65.mp3",
    66: "/assets/audio/miguel/66.mp3",
    67: "/assets/audio/miguel/67.mp3",
    68: "/assets/audio/miguel/68.mp3",
    69: "/assets/audio/miguel/69.mp3",
    70: "/assets/audio/miguel/70.mp3",
    71: "/assets/audio/miguel/71.mp3",
    72: "/assets/audio/miguel/72.mp3",
    73: "/assets/audio/miguel/73.mp3",
    74: "/assets/audio/miguel/74.mp3",
    75: "/assets/audio/miguel/75.mp3",
    hotball: "/assets/audio/miguel/hotball.mp3",
    byitself: "/assets/audio/miguel/byitself.mp3",
    letsplaybingo: "/assets/audio/miguel/letsplaybingo.mp3",
    b: "/assets/audio/miguel/b.mp3",
    i: "/assets/audio/miguel/i.mp3",
    n: "/assets/audio/miguel/n.mp3",
    g: "/assets/audio/miguel/g.mp3",
    o: "/assets/audio/miguel/o.mp3",
    underthe: "/assets/audio/miguel/underthe.mp3",
  };

  const penelope = {
    0: "/assets/audio/penelope/0.mp3",
    1: "/assets/audio/penelope/1.mp3",
    2: "/assets/audio/penelope/2.mp3",
    3: "/assets/audio/penelope/3.mp3",
    4: "/assets/audio/penelope/4.mp3",
    5: "/assets/audio/penelope/5.mp3",
    6: "/assets/audio/penelope/6.mp3",
    7: "/assets/audio/penelope/7.mp3",
    8: "/assets/audio/penelope/8.mp3",
    9: "/assets/audio/penelope/9.mp3",
    10: "/assets/audio/penelope/10.mp3",
    11: "/assets/audio/penelope/11.mp3",
    12: "/assets/audio/penelope/12.mp3",
    13: "/assets/audio/penelope/13.mp3",
    14: "/assets/audio/penelope/14.mp3",
    15: "/assets/audio/penelope/15.mp3",
    16: "/assets/audio/penelope/16.mp3",
    17: "/assets/audio/penelope/17.mp3",
    18: "/assets/audio/penelope/18.mp3",
    19: "/assets/audio/penelope/19.mp3",
    20: "/assets/audio/penelope/20.mp3",
    21: "/assets/audio/penelope/21.mp3",
    22: "/assets/audio/penelope/22.mp3",
    23: "/assets/audio/penelope/23.mp3",
    24: "/assets/audio/penelope/24.mp3",
    25: "/assets/audio/penelope/25.mp3",
    26: "/assets/audio/penelope/26.mp3",
    27: "/assets/audio/penelope/27.mp3",
    28: "/assets/audio/penelope/28.mp3",
    29: "/assets/audio/penelope/29.mp3",
    30: "/assets/audio/penelope/30.mp3",
    31: "/assets/audio/penelope/31.mp3",
    32: "/assets/audio/penelope/32.mp3",
    33: "/assets/audio/penelope/33.mp3",
    34: "/assets/audio/penelope/34.mp3",
    35: "/assets/audio/penelope/35.mp3",
    36: "/assets/audio/penelope/36.mp3",
    37: "/assets/audio/penelope/37.mp3",
    38: "/assets/audio/penelope/38.mp3",
    39: "/assets/audio/penelope/39.mp3",
    40: "/assets/audio/penelope/40.mp3",
    41: "/assets/audio/penelope/41.mp3",
    42: "/assets/audio/penelope/42.mp3",
    43: "/assets/audio/penelope/43.mp3",
    44: "/assets/audio/penelope/44.mp3",
    45: "/assets/audio/penelope/45.mp3",
    46: "/assets/audio/penelope/46.mp3",
    47: "/assets/audio/penelope/47.mp3",
    48: "/assets/audio/penelope/48.mp3",
    49: "/assets/audio/penelope/49.mp3",
    50: "/assets/audio/penelope/50.mp3",
    51: "/assets/audio/penelope/51.mp3",
    52: "/assets/audio/penelope/52.mp3",
    53: "/assets/audio/penelope/53.mp3",
    54: "/assets/audio/penelope/54.mp3",
    55: "/assets/audio/penelope/55.mp3",
    56: "/assets/audio/penelope/56.mp3",
    57: "/assets/audio/penelope/57.mp3",
    58: "/assets/audio/penelope/58.mp3",
    59: "/assets/audio/penelope/59.mp3",
    60: "/assets/audio/penelope/60.mp3",
    61: "/assets/audio/penelope/61.mp3",
    62: "/assets/audio/penelope/62.mp3",
    63: "/assets/audio/penelope/63.mp3",
    64: "/assets/audio/penelope/64.mp3",
    65: "/assets/audio/penelope/65.mp3",
    66: "/assets/audio/penelope/66.mp3",
    67: "/assets/audio/penelope/67.mp3",
    68: "/assets/audio/penelope/68.mp3",
    69: "/assets/audio/penelope/69.mp3",
    70: "/assets/audio/penelope/70.mp3",
    71: "/assets/audio/penelope/71.mp3",
    72: "/assets/audio/penelope/72.mp3",
    73: "/assets/audio/penelope/73.mp3",
    74: "/assets/audio/penelope/74.mp3",
    75: "/assets/audio/penelope/75.mp3",
    hotball: "/assets/audio/penelope/hotball.mp3",
    byitself: "/assets/audio/penelope/byitself.mp3",
    letsplaybingo: "/assets/audio/penelope/letsplaybingo.mp3",
    b: "/assets/audio/penelope/b.mp3",
    i: "/assets/audio/penelope/i.mp3",
    n: "/assets/audio/penelope/n.mp3",
    g: "/assets/audio/penelope/g.mp3",
    o: "/assets/audio/penelope/o.mp3",
    underthe: "/assets/audio/penelope/underthe.mp3",
  };

  const salli = {
    0: "/assets/audio/salli/0.mp3",
    1: "/assets/audio/salli/1.mp3",
    2: "/assets/audio/salli/2.mp3",
    3: "/assets/audio/salli/3.mp3",
    4: "/assets/audio/salli/4.mp3",
    5: "/assets/audio/salli/5.mp3",
    6: "/assets/audio/salli/6.mp3",
    7: "/assets/audio/salli/7.mp3",
    8: "/assets/audio/salli/8.mp3",
    9: "/assets/audio/salli/9.mp3",
    10: "/assets/audio/salli/10.mp3",
    11: "/assets/audio/salli/11.mp3",
    12: "/assets/audio/salli/12.mp3",
    13: "/assets/audio/salli/13.mp3",
    14: "/assets/audio/salli/14.mp3",
    15: "/assets/audio/salli/15.mp3",
    16: "/assets/audio/salli/16.mp3",
    17: "/assets/audio/salli/17.mp3",
    18: "/assets/audio/salli/18.mp3",
    19: "/assets/audio/salli/19.mp3",
    20: "/assets/audio/salli/20.mp3",
    21: "/assets/audio/salli/21.mp3",
    22: "/assets/audio/salli/22.mp3",
    23: "/assets/audio/salli/23.mp3",
    24: "/assets/audio/salli/24.mp3",
    25: "/assets/audio/salli/25.mp3",
    26: "/assets/audio/salli/26.mp3",
    27: "/assets/audio/salli/27.mp3",
    28: "/assets/audio/salli/28.mp3",
    29: "/assets/audio/salli/29.mp3",
    30: "/assets/audio/salli/30.mp3",
    31: "/assets/audio/salli/31.mp3",
    32: "/assets/audio/salli/32.mp3",
    33: "/assets/audio/salli/33.mp3",
    34: "/assets/audio/salli/34.mp3",
    35: "/assets/audio/salli/35.mp3",
    36: "/assets/audio/salli/36.mp3",
    37: "/assets/audio/salli/37.mp3",
    38: "/assets/audio/salli/38.mp3",
    39: "/assets/audio/salli/39.mp3",
    40: "/assets/audio/salli/40.mp3",
    41: "/assets/audio/salli/41.mp3",
    42: "/assets/audio/salli/42.mp3",
    43: "/assets/audio/salli/43.mp3",
    44: "/assets/audio/salli/44.mp3",
    45: "/assets/audio/salli/45.mp3",
    46: "/assets/audio/salli/46.mp3",
    47: "/assets/audio/salli/47.mp3",
    48: "/assets/audio/salli/48.mp3",
    49: "/assets/audio/salli/49.mp3",
    50: "/assets/audio/salli/50.mp3",
    51: "/assets/audio/salli/51.mp3",
    52: "/assets/audio/salli/52.mp3",
    53: "/assets/audio/salli/53.mp3",
    54: "/assets/audio/salli/54.mp3",
    55: "/assets/audio/salli/55.mp3",
    56: "/assets/audio/salli/56.mp3",
    57: "/assets/audio/salli/57.mp3",
    58: "/assets/audio/salli/58.mp3",
    59: "/assets/audio/salli/59.mp3",
    60: "/assets/audio/salli/60.mp3",
    61: "/assets/audio/salli/61.mp3",
    62: "/assets/audio/salli/62.mp3",
    63: "/assets/audio/salli/63.mp3",
    64: "/assets/audio/salli/64.mp3",
    65: "/assets/audio/salli/65.mp3",
    66: "/assets/audio/salli/66.mp3",
    67: "/assets/audio/salli/67.mp3",
    68: "/assets/audio/salli/68.mp3",
    69: "/assets/audio/salli/69.mp3",
    70: "/assets/audio/salli/70.mp3",
    71: "/assets/audio/salli/71.mp3",
    72: "/assets/audio/salli/72.mp3",
    73: "/assets/audio/salli/73.mp3",
    74: "/assets/audio/salli/74.mp3",
    75: "/assets/audio/salli/75.mp3",
    hotball: "/assets/audio/salli/hotball.mp3",
    byitself: "/assets/audio/salli/byitself.mp3",
    letsplaybingo: "/assets/audio/salli/letsplaybingo.mp3",
    b: "/assets/audio/salli/b.mp3",
    i: "/assets/audio/salli/i.mp3",
    n: "/assets/audio/salli/n.mp3",
    g: "/assets/audio/salli/g.mp3",
    o: "/assets/audio/salli/o.mp3",
    underthe: "/assets/audio/salli/underthe.mp3",
  };

  return [
    { label: "Matthew (US English)", lang: "en-US", value: "Matthew", audio: matthew },
    { label: "Salli (US English)", lang: "en-US", value: "Salli", audio: salli },
    { label: "Mathieu (French)", lang: "fr", value: "Mathieu", audio: mathieu },
    { label: "Lea (French)", lang: "fr", value: "Lea", audio: lea },
    { label: "Miguel (Spanish)", lang: "es", value: "Miguel", audio: miguel },
    { label: "Penelope (Spanish)", lang: "es", value: "Penelope", audio: penelope },
  ];
}

/**
 * Returns language label for use with the speech synthesis api
 *
 * @var  {String} Language code
 */
export function getLanguageLabel(code) {
  switch (code) {
    case "ar-SA":
      return "Arabic (Saudi Arabia)";
    case "cs-CZ":
      return "Czech (Czech Republic)";
    case "da-DK":
      return "Danish (Denmark)";
    case "de-DE":
      return "German";
    case "el-GR":
      return "Greek (Greece)";
    case "en":
      return "English";
    case "en-AU":
      return "English (Australia)";
    case "en-GB":
      return "UK English";
    case "en-IE":
      return "English (Ireland)";
    case "en-IN":
      return "English (India)";
    case "en-US":
      return "US English";
    case "en-ZA":
      return "English (South Africa)";
    case "es-AR":
      return "Spanish (Argentina)";
    case "es-ES":
      return "Spanish (Spain)";
    case "es-MX":
      return "Spanish (Mexico)";
    case "es-US":
      return "Spanish (United States)";
    case "fi-FI":
      return "Finnish (Finland)";
    case "fr-CA":
      return "French (Canada)";
    case "fr-FR":
      return "French (France)";
    case "he-IL":
      return "Hebrew";
    case "hi-IN":
      return "Hindi (India)";
    case "hu-HU":
      return "Hungarian (Hungary)";
    case "id-ID":
      return "Indonesian";
    case "it-IT":
      return "Italian";
    case "ja-JP":
      return "Japanese";
    case "ko-KR":
      return "Korean (Korea)";
    case "nb-NO":
      return "Norwegian (Bokm?l) (Norway)";
    case "nl-BE":
      return "Dutch (Belgium)";
    case "nl-NL":
      return "Dutch (Netherlands)";
    case "pl-PL":
      return "Polish (Poland)";
    case "pt-PT":
      return "Portuguese (Portugal)";
    case "pt-BR":
      return "Portuguese (Brazil)";
    case "ro-RO":
      return "Romanian (Romania)";
    case "ru-RU":
      return "Russian (Russia)";
    case "sk-SK":
      return "Slovak (Slovakia)";
    case "sv-SE":
      return "Swedish";
    case "th-TH":
      return "Thai (Thailand)";
    case "tr-TR":
      return "Turkish (Turkey)";
    case "zh-CN":
      return "Chinese (S)";
    case "zh-HK":
      return "Chinese (Hong Kong)";
    case "zh-TW":
      return "Chinese (T)";
    default:
      return code;
  }
}

/**
 * Returns a list of options for selecting chimes
 *
 * @return  {Array}  Array of options used in the chimes select
 */
export function getChimeOptions() {
  return [
    { label: "Chime 1", value: "/assets/chimes/chime1.mp3" },
    { label: "Chime 2", value: "/assets/chimes/chime2.mp3" },
    { label: "Chime 3", value: "/assets/chimes/chime3.mp3" },
    { label: "Chime 4", value: "/assets/chimes/chime4.mp3" },
    { label: "Chime 5", value: "/assets/chimes/chime5.mp3" },
    { label: "Chime 6", value: "/assets/chimes/chime6.mp3" },
    { label: "Chime 7", value: "/assets/chimes/chime7.mp3" },
    { label: "Chime 8", value: "/assets/chimes/chime8.mp3" },
    { label: "Chime 9", value: "/assets/chimes/chime9.mp3" },
    { label: "Chime 10", value: "/assets/chimes/chime10.mp3" },
    { label: "Chime 11", value: "/assets/chimes/chime11.mp3" },
    { label: "Chime 12", value: "/assets/chimes/chime12.mp3" },
    { label: "Pop 1", value: "/assets/chimes/pop1.mp3" },
    { label: "Pop 2", value: "/assets/chimes/pop2.mp3" },
    { label: "Pop 3", value: "/assets/chimes/pop3.mp3" },
    { label: "Pop 4", value: "/assets/chimes/pop4.mp3" },
  ];
}

// Ball Display Methods ------------------------------------
/**
 * Returns the ball color associated with the passed in letter
 *
 * @param   {String}  letter  Letter in "BINGO"
 *
 * @return  {String}          Returns the color string
 */
export function getBallColor(letter) {
  switch (letter) {
    case "B":
      return "blue";
    case "I":
      return "red";
    case "N":
      return "white";
    case "G":
      return "green";
    case "O":
      return "yellow";
    default:
      return "white";
  }
}

/**
 * Returns a bingo ball display using the selected ball object
 *
 * @return  {JSX}  JSX element
 */
export function getBallDisplay(ball) {
  if (ball) {
    return (
      <div className={"ball-display " + ball.color + " relative notranslate"}>
        <div className="content">
          <div className="ball-content">
            <div className="ball-letter">{ball.letter}</div>
            <div className="ball-number">{ball.number}</div>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="ball-display white relative notranslate">
        <div className="content">
          <div className="ball-content">{getTextLogo()}</div>
        </div>
      </div>
    );
  }
}

// Layout & Theme Methods ----------------------------------

export function getCardColorOptions() {
  return [
    { value: "amber", label: "Amber" },
    { value: "amber-screen", label: "Amber Screen" },
    { value: "aqua", label: "Aqua" },
    { value: "aqua-screen", label: "Aqua Screen" },
    { value: "black", label: "Black" },
    { value: "black-screen", label: "Black Screen" },
    { value: "blue", label: "Blue" },
    { value: "blue-screen", label: "Blue Screen" },
    { value: "brown", label: "Brown" },
    { value: "brown-screen", label: "Brown Screen" },
    { value: "chartreuse", label: "Chartreuse" },
    { value: "chartreuse-screen", label: "Chartreuse Screen" },
    { value: "gray", label: "Gray" },
    { value: "gray-screen", label: "Gray Screen" },
    { value: "green", label: "Green" },
    { value: "green-screen", label: "Green Screen" },
    { value: "lime", label: "Lime" },
    { value: "lime-screen", label: "Lime Screen" },
    { value: "magenta", label: "Magenta" },
    { value: "magenta-screen", label: "Magenta Screen" },
    { value: "olive", label: "Olive" },
    { value: "olive-screen", label: "Olive Screen" },
    { value: "orange", label: "Orange" },
    { value: "orange-screen", label: "Orange Screen" },
    { value: "pink", label: "Pink" },
    { value: "pink-screen", label: "Pink Screen" },
    { value: "purple", label: "Purple" },
    { value: "purple-screen", label: "Purple Screen" },
    { value: "rainbow", label: "Rainbow" },
    { value: "rainbow-screen", label: "Rainbow Screen" },
    { value: "red", label: "Red" },
    { value: "red-screen", label: "Red Screen" },
    { value: "tan", label: "Tan" },
    { value: "tan-screen", label: "Tan Screen" },
    { value: "teal", label: "Teal" },
    { value: "teal-screen", label: "Teal Screen" },
    { value: "vermillion", label: "Vermillion" },
    { value: "vermillion-screen", label: "Vermillion Screen" },
    { value: "violet", label: "Violet" },
    { value: "violet-screen", label: "Violet Screen" },
    { value: "yellow", label: "Yellow" },
    { value: "yellow-screen", label: "Yellow Screen" },
  ];
}

export function getDauberColorOptions() {
  return [
    { value: "amber", label: "Amber" },
    { value: "blue", label: "Blue" },
    { value: "brown", label: "Brown" },
    { value: "chartreuse", label: "Chartreuse" },
    { value: "green", label: "Green" },
    { value: "magenta", label: "Magenta" },
    { value: "orange", label: "Orange" },
    { value: "pink", label: "Pink" },
    { value: "purple", label: "Purple" },
    { value: "rainbow", label: "Rainbow" },
    { value: "red", label: "Red" },
    { value: "teal", label: "Teal" },
    { value: "vermillion", label: "Vermillion" },
    { value: "violet", label: "Violet" },
    { value: "yellow", label: "Yellow" },
  ];
}

/**
 * Returns a list of options for layouts
 * For use in a dropdown menu
 *
 * @return  {Array}  Layout options
 */
export function getLayoutOptions() {
  return [
    { value: "classic", label: "Classic" },
    { value: "classic-reverse", label: "Classic Reversed" },
    { value: "easy-view", label: "Easy View" },
    { value: "legacy-edition", label: "Legacy Edition" },
    { value: "ninety-ball", label: "90 Ball" },
    { value: "simple", label: "Simple" },
    { value: "stacked", label: "Stacked" },
    { value: "vertical", label: "Vertical" },
  ];
}

/**
 * Returns a list of theme options
 * for use in a dropdown menu
 *
 * @return  {Array}  List of options for themes
 */
export function getThemeOptions() {
  return [
    { value: "lpb", label: "Let's Play Bingo" },
    { value: "amber", label: "Amber" },
    { value: "black", label: "Black" },
    { value: "blue", label: "Blue" },
    { value: "brown", label: "Brown" },
    { value: "chartreuse", label: "Chartreuse" },
    { value: "gray", label: "Gray" },
    { value: "green", label: "Green" },
    { value: "magenta", label: "Magenta" },
    { value: "orange", label: "Orange" },
    { value: "pink", label: "Pink" },
    { value: "purple", label: "Purple" },
    { value: "rainbow", label: "Rainbow" },
    { value: "red", label: "Red" },
    { value: "teal", label: "Teal" },
    { value: "vermillion", label: "Vermillion" },
    { value: "violet", label: "Violet" },
    { value: "yellow", label: "Yellow" },
  ];
}

// Logo Methods --------------------------------------------

export function getColorTextLogo() {
  return (
    <svg className="logo" data-name="text-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 420 220">
      <title>Let's Play Bingo Logo</title>
      <g id="Bingo">
        <path
          className="logo-white"
          d="m41.86,84.03c.97-.04,1.81-.03,2.5.04l2.06.2c.14,1.4-.48,2.73-1.85,3.99-1.37,1.26-3.01,2.35-4.9,3.27-1.9.93-3.78,1.68-5.64,2.26-1.86.58-3.28.9-4.26.94-1.26.16-2.36-.05-3.27-.63-.92-.57-1.68-1.31-2.28-2.2-1.88-3.52-2.49-7.25-1.82-11.22.66-3.96,2.05-7.85,4.16-11.68,2.11-3.82,4.68-7.42,7.72-10.81,3.04-3.38,5.98-6.19,8.84-8.41,7.2-5.41,14.74-9.78,22.63-13.1,7.89-3.32,16.15-5.75,24.77-7.27,3.94-.58,8.27-1.1,12.96-1.55,4.7-.44,9.32-.55,13.89-.31,4.56.24,8.84.86,12.84,1.88,4,1.02,7.41,2.67,10.25,4.96,2.83,2.29,4.83,5.3,5.99,9.02,1.16,3.73,1.13,8.38-.11,13.95-1.56,4.57-3.55,8.58-5.97,12.02-2.42,3.45-5.09,6.55-7.99,9.32-2.91,2.77-5.9,5.29-8.97,7.56-3.07,2.27-5.96,4.45-8.66,6.55,4.18-.15,8.32.47,12.42,1.84,4.1,1.37,7.81,3.26,11.11,5.67,3.31,2.41,6.11,5.32,8.42,8.74,2.3,3.42,3.75,7.1,4.34,11.05.73,8.13-.57,15.81-3.92,23.05-3.35,7.25-7.86,13.96-13.53,20.14-5.67,6.18-12.01,11.74-19.02,16.68-7.01,4.94-13.81,9.17-20.41,12.69-5.44,3.08-11.95,6.12-19.54,9.13-7.58,3.01-15.28,5.35-23.08,7.02-7.8,1.67-15.21,2.44-22.2,2.31-7-.13-12.7-1.86-17.1-5.21-2.45-1.91-4.18-4.13-5.18-6.65-1-2.53-1.38-5.07-1.13-7.61s1.08-5.07,2.51-7.57c1.42-2.5,3.28-4.82,5.58-6.96,4.76-4.68,9.94-8.68,15.55-12.02,5.6-3.34,11.45-6.31,17.54-8.91,5.22-2.27,10.57-4.07,16.05-5.41,5.48-1.34,10.97-2.5,16.49-3.49,4.8-.78,9.42-1.23,13.88-1.35.41.04.83.05,1.25.02.42-.03.76.04,1.02.2.69.07,1.08.31,1.18.74.1.43-.09.9-.55,1.4-.3.25-.71.56-1.23.92-.52.37-1,.67-1.44.9-5.07,2.14-10.15,3.98-15.22,5.49-5.08,1.52-10.15,3.35-15.22,5.49-4.34,1.8-8.69,3.67-13.05,5.6-4.36,1.94-8.59,4.06-12.7,6.36-4.12,2.31-7.97,4.85-11.58,7.62-3.6,2.77-6.82,5.82-9.64,9.16-.82,1.31-1.43,2.53-1.82,3.68-.39,1.14.13,2.2,1.56,3.17,3.3,1.71,7.73,2.28,13.27,1.71,5.54-.57,11.42-1.77,17.64-3.59,6.22-1.82,12.31-4.08,18.26-6.76,5.95-2.68,10.94-5.36,14.95-8.02,6.74-3.65,13.26-8.5,19.55-14.55,6.28-6.05,11.42-12.46,15.42-19.22,3.99-6.76,6.34-13.41,7.05-19.94.71-6.53-1.22-12.03-5.78-16.5-1.24-1.51-2.86-2.68-4.87-3.5s-4.17-1.41-6.49-1.78c-2.33-.36-4.68-.49-7.05-.38-2.37.12-4.41.37-6.12.76-3.28.79-6.54,1.9-9.8,3.31-3.26,1.42-6.39,3.16-9.36,5.23.31-3.16,1.69-6.29,4.15-9.39,2.45-3.09,5.34-6.04,8.67-8.84,3.33-2.8,6.74-5.42,10.25-7.85,3.5-2.44,6.38-4.59,8.65-6.45,2.9-2.63,5.67-5.83,8.33-9.6,2.66-3.77,4.59-7.57,5.8-11.41,1.21-3.84,1.46-7.46.75-10.86-.71-3.4-2.91-6.19-6.58-8.35-2.77-1.52-5.87-2.45-9.31-2.78s-6.95-.26-10.54.22c-3.59.48-7.14,1.25-10.64,2.3-3.51,1.05-6.7,2.13-9.59,3.23-4.18,1.54-8.24,3.67-12.19,6.41-3.95,2.74-7.38,5.91-10.3,9.52-2.92,3.6-5.14,7.49-6.66,11.64-1.52,4.16-2.07,8.41-1.66,12.75Zm1.44,68.47c-3,.82-6.59,2.45-10.79,4.88-4.2,2.44-8.46,5.18-12.78,8.23.48-3.56,1.68-8.38,3.6-14.44,1.91-6.06,4.09-12.38,6.54-18.95,2.44-6.56,4.9-12.85,7.36-18.86,2.46-6.01,4.49-10.81,6.09-14.41,4.11-7.93,8.25-15.55,12.44-22.85,4.18-7.3,9.49-13.97,15.91-20.01,3.97-3.64,8.75-5.74,14.37-6.31,1.83-.24,3.55-.07,5.16.5,1.76.45,3.1,1.34,4.01,2.68.91,1.34.99,2.95.26,4.82-.07.69-.27,1.33-.6,1.92-.34.59-.68,1.29-1.04,2.09-.66,1.05-1.34,1.99-2.05,2.82-.71.84-1.38,1.71-2.03,2.61-5.69,7.08-10.83,14.15-15.4,21.2-4.58,7.05-8.71,14.15-12.39,21.29-3.69,7.14-7.05,14.28-10.11,21.41-3.06,7.13-5.91,14.25-8.54,21.35Z"
        />
        <g>
          <path
            className="logo-white"
            d="m190.31,143.73c0-2-2-1-2-1-2,1-2.16,2.4-4.84,5.16-2.69,2.76-5.1,4.39-7.26,4.88-1.84.47-3.08.1-3.71-1.11-.63-1.2-.91-2.62-.85-4.24.56-4.29,1.69-8.65,3.38-13.09,1.7-4.44,3.37-9.15,5.02-14.14.66-1.97.91-3.86.75-5.67,0-1.11-.27-2.09-.81-2.95-.55-.86-1.19-1.56-1.91-2.1-.73-.54-1.58-.94-2.54-1.21-.97-.27-1.86-.36-2.7-.29-3.84.34-6.87,2.13-9.1,5.35-.46.75-.89,1.52-1.29,2.31-.4.79-.77,1.61-1.12,2.44-1.94,5.22-3.68,10.34-5.22,15.37-1.54,5.03-2.11,10.4-1.7,16.12.16,2.91.9,5.44,2.25,7.59,1.34,2.15,3.08,3.78,5.22,4.91,2.14,1.12,4.56,1.61,7.28,1.47,2.71-.14,5.5-1.05,8.39-2.72,2.67-1.75,5.69-3.79,8.1-6.93q1.66-2.16,2.66-4.16c2-4,2-6,2-6h0Z"
          />
          <path className="logo-white" d="m165.76,101.3c1.4,1.59,3.06,2.37,5,2.35,1.93-.02,3.82-.57,5.66-1.64,1.84-1.07,3.47-2.5,4.88-4.29,1.41-1.79,2.29-3.61,2.65-5.46.52-3.58-.24-5.88-2.27-6.91-2.03-1.03-4.84-1.18-8.44-.46-2.15.49-3.98,1.39-5.49,2.68-1.51,1.3-2.61,2.73-3.31,4.3-.7,1.58-.97,3.19-.82,4.84.15,1.65.86,3.18,2.14,4.58Z" />
        </g>
        <path
          className="logo-white"
          d="m243.48,96.27c2.88,1.76,4.75,3.91,5.6,6.46.86,2.55,1.05,5.38.6,8.5-.46,3.12-1.31,6.47-2.56,10.06s-2.55,7.21-3.9,10.86c-1.35,3.65-2.54,7.29-3.58,10.91-1.04,3.63-1.47,7.04-1.28,10.25.28,1.99,1.35,3.08,3.2,3.27,1.85.19,3.66-.3,5.44-1.47.8-.48,1.53-.99,2.22-1.56.68-.57,1.36-1.13,2.05-1.7.4-.24.74-.55,1.02-.92.28-.38.67-.64,1.19-.79.53.05.71.34.55.86-.36.74-.65,1.47-.9,2.2-.25.73-.61,1.42-1.07,2.06-.46.75-.95,1.39-1.47,1.95-.53.55-1.03,1.1-1.5,1.65-1.54,1.55-3.57,2.79-6.11,3.72-2.53.93-4.75,1.28-6.66,1.05-5.26-.24-8.88-1.83-10.87-4.78-1.99-2.95-2.97-6.6-2.94-10.93.03-4.34.71-9.01,2.05-14.03,1.33-5.01,2.74-9.7,4.24-14.07s2.68-8.06,3.57-11.06c.88-3,.89-4.67.03-5-1.98.08-4.1,1.02-6.34,2.84-2.25,1.82-4.41,3.98-6.49,6.48-2.08,2.51-3.93,5.19-5.56,8.06s-2.81,5.45-3.55,7.73c-1.78,3.59-3.26,6.92-4.45,10.01-1.19,3.08-2.41,6.45-3.65,10.09-.34.84-.75,1.56-1.22,2.15-.47.6-1.11,1.03-1.92,1.31-1.95.48-3.68.43-5.19-.14-1.76-.95-3-2.53-3.72-4.74-.72-2.21-1.14-4.56-1.26-7.08-.12-2.51-.07-4.96.13-7.35.21-2.39.4-4.3.59-5.73.45-3.17,1.03-6.37,1.73-9.61.71-3.24,1.48-6.34,2.31-9.29.84-2.95,1.8-5.66,2.88-8.13,1.09-2.47,2.24-4.51,3.46-6.13,1.45-2.55,3.39-4.24,5.83-5.06,1.83-.57,3.45-.63,4.85-.21,1.4.43,2.43,1.47,3.11,3.13.83,2.25,1.02,4.37.57,6.38s-1,4.05-1.65,6.12c-.42,1.15-.8,2.17-1.14,3.05-.34.89-.68,1.78-1.02,2.66-.34.89-.66,1.77-.94,2.66-.29.88-.59,1.94-.89,3.18,2.16-3.92,4.72-7.51,7.67-10.75,2.95-3.24,6.02-5.83,9.2-7.78,3.18-1.95,6.32-3.08,9.43-3.41,3.11-.33,5.89.36,8.35,2.05Z"
        />
        <path
          className="logo-white"
          d="m286.08,164.93c.05.6-.11,1.45-.48,2.54s-.83,2.19-1.35,3.3c-.53,1.11-1.07,2.11-1.61,3.02-.55.9-.96,1.52-1.25,1.85-3.45-1.11-6.99-1.52-10.63-1.25-3.64.27-6.96,1.15-9.97,2.63-3.01,1.48-5.5,3.49-7.47,6.04-1.97,2.54-2.96,5.58-2.96,9.12.76,2.66,2.28,4.46,4.57,5.42,2.28.95,4.76,1.11,7.42.47,4.89-1.55,9.05-3.81,12.5-6.79,3.45-2.98,6.67-7.03,9.67-12.14,1.6-3.17,3.05-6.58,4.35-10.22,1.3-3.65,2.41-7.35,3.33-11.12.92-3.77,1.74-7.52,2.45-11.26.71-3.74,1.33-7.36,1.86-10.83-2.01,2.1-4.08,4.17-6.2,6.23-2.12,2.06-4.28,3.99-6.47,5.8-2.19,1.81-4.44,3.37-6.73,4.69-2.29,1.32-4.61,2.28-6.96,2.89-1.73.56-3.51.87-5.33.93-1.83.06-3.54-.11-5.15-.52-1.61-.41-3.05-1.09-4.34-2.03-1.29-.94-2.21-2.15-2.76-3.61-1.08-2.73-1.65-5.6-1.71-8.62-.06-3.02.25-6.02.92-9.01.68-2.98,1.67-5.95,2.98-8.89,1.31-2.94,2.74-5.69,4.29-8.25,3.87-5.89,8.76-11.15,14.69-15.76,5.92-4.61,12.1-7.76,18.55-9.45,2.35-.61,4.61-.86,6.76-.75,2.15.11,4.18.79,6.07,2.03.55.36,1.1.66,1.65.91.54.25,1.09.56,1.65.91,3.25,2.43,4.76,5.78,4.51,10.04-.12,1.02-.26,2.04-.43,3.07-.17,1.02-.52,2.07-1.06,3.12-.72,1.27-1.38,1.84-1.97,1.69-.59-.15-1.03-.67-1.31-1.55-.44-1.37-.79-2.68-1.06-3.92-.27-1.24-.86-2.32-1.78-3.25-.62-1.05-1.4-1.62-2.35-1.68-.95-.07-1.88.04-2.8.33-2.94,1.07-5.87,2.59-8.78,4.57-2.91,1.98-5.42,4.07-7.53,6.27-2,2.2-4,4.7-6,7.5-2,2.8-3.71,5.65-5.12,8.55-1.42,2.9-2.45,5.74-3.09,8.52-.64,2.78-.61,5.25.11,7.4.63,1.26,1.65,1.74,3.03,1.47s2.83-.88,4.31-1.82c1.49-.94,2.91-2,4.27-3.18,1.36-1.18,2.33-2.05,2.91-2.6,3.2-2.81,6.4-5.72,9.58-8.72,3.18-3.01,6.06-5.38,8.63-7.12,1.07-.9,2.22-1.46,3.46-1.67,1.24-.21,2.49-.27,3.75-.18,1.58.16,2.44.99,2.58,2.49.46,1.67.76,3.26.9,4.76.15,2.91,0,5.9-.46,8.97-.46,3.07-1.03,6.09-1.7,9.08-.97,4.42-1.95,8.62-2.96,12.6-1.01,3.97-2.22,7.99-3.63,12.05,8.53,4.08,16.1,6.51,22.71,7.28,6.61.77,12.05.41,16.31-1.08,2.92-1.27,5.16-2.86,6.72-4.76,1.56-1.9,2.36-4.32,2.41-7.25,1.15,1.21,1.77,2.59,1.86,4.15.08,1.56-.22,3.12-.92,4.7-.7,1.58-1.69,3.1-2.97,4.58-1.28,1.48-2.61,2.68-3.99,3.61-2.67,1.75-5.73,2.98-9.17,3.69-3.44.71-7.11.99-10.99.83-3.89-.16-7.94-.71-12.16-1.64-4.22-.94-8.49-2.22-12.82-3.85-4.2,6.93-9.16,12.92-14.88,17.97-5.73,5.05-12.6,8.39-20.62,10.01-3.2.49-6.21.48-9.03-.03-2.82-.51-5.29-1.5-7.41-2.97-2.12-1.48-3.75-3.37-4.9-5.69-1.15-2.32-1.65-5.05-1.52-8.19.4-4.88,2.08-9.19,5.05-12.93,2.96-3.75,6.54-6.79,10.73-9.13,4.19-2.34,8.7-3.9,13.55-4.69,4.85-.79,9.41-.66,13.69.37Z"
        />
        <path
          className="logo-white"
          d="m317.84,126.44c1.47-6.33,4.4-12.41,8.8-18.25,2.15-2.76,4.58-5.24,7.3-7.43,2.72-2.19,5.68-3.81,8.87-4.86,1.9-.6,3.64-.78,5.22-.54.71.07,1.32.31,1.83.72.93.6.74,1.26-.55,1.97-1.94,1.07-3.77,2.89-5.48,5.47-1.71,2.58-3.17,5.51-4.37,8.78-1.2,3.27-2.11,6.64-2.72,10.11-.61,3.47-.85,6.68-.71,9.62.13,2.94.73,5.36,1.79,7.25,1.06,1.89,2.62,2.86,4.68,2.91,1.87-.41,3.7-1.52,5.51-3.34,1.8-1.82,3.45-4.06,4.96-6.74,1.5-2.68,2.82-5.66,3.96-8.94,1.14-3.29,2.03-6.56,2.67-9.82.64-3.26,1.02-6.39,1.15-9.39.12-3-.1-5.6-.66-7.78-.5-1.49-1.32-2.65-2.45-3.49-1.13-.83-2.39-1.41-3.77-1.74-1.38-.32-2.84-.42-4.36-.28-1.53.14-2.99.45-4.37.93-1.16.48-2.35,1.03-3.56,1.67-1.21.63-2.37,1.27-3.47,1.9-.19.08-.4.19-.63.33-.23.14-.45.21-.68.21-.39.16-.61.16-.68,0-.13-.3-.16-.52-.09-.63.46-.81.98-1.48,1.56-1.98,2.08-2.11,4.37-3.97,6.88-5.58,2.51-1.61,5.23-2.84,8.16-3.7,4.7-.95,8.61-.54,11.71,1.22,3.1,1.76,5.46,4.19,7.08,7.29,1.54,3.75,2.24,7.78,2.09,12.09-.15,4.31-1,8.64-2.55,12.99-1.55,4.35-3.71,8.6-6.48,12.73-2.77,4.14-6,7.91-9.7,11.31-2.56,2.31-5.39,4.17-8.49,5.57-3.1,1.41-6.17,2.34-9.22,2.79-3.04.45-5.88.36-8.51-.25-2.63-.62-4.71-1.82-6.24-3.6-2.52-2.81-4.09-5.88-4.72-9.19-.63-3.32-.54-6.75.26-10.31Z"
        />
      </g>
      <path
        id="_"
        data-name="!"
        className="logo-white"
        d="m388.36,124.46c1.51-.44,2.83-.52,3.95-.24,1.12.28,2.11.76,2.97,1.43,1.85,1.53,3.13,3.53,3.86,6.03,1.24,4.26.53,7.75-2.15,10.45-.89.9-2.07,1.56-3.53,1.99-3.37.98-6.14.79-8.34-.56-2.19-1.35-3.63-3.21-4.32-5.57-.82-2.82-.6-5.49.68-7.99,1.44-2.77,3.74-4.61,6.88-5.53Zm28.59-81.65c2.48-.53,4.61,1.82,3.82,4.24-3.51,10.69-6.69,21.41-9.52,32.15-3.13,11.87-6.24,23.8-9.32,35.79-.34,1.32-1.46,2.31-2.81,2.45-2.15.23-4.29.51-6.4.84-1.36.21-2.73.42-4.1.64-2.22.34-4.12-1.59-3.74-3.8,1.78-10.47,3.49-20.93,5.13-31.39,1.76-11.25,3.64-22.56,5.65-33.92.23-1.3,1.2-2.33,2.48-2.63s2.73-.64,4.29-.99c2.45-.55,4.89-1.12,7.33-1.72,2.44-.59,4.82-1.14,7.12-1.63.02,0,.05,0,.07-.01Z"
      />
      <g>
        <path className="logo-color" d="m160.29,40.37h7.08v22.6h12.84v6h-19.92v-28.6Z" />
        <path className="logo-color" d="m183.02,40.37h21.68v5.84h-14.6v5.12h12.52v5.52h-12.52v6.12h14.84v6h-21.92v-28.6Z" />
        <path className="logo-color" d="m215.6,46.36h-8.32v-6h23.8v6h-8.4v22.6h-7.08v-22.6Z" />
        <path className="logo-color" d="m233.5,49.92c1.56-.48,2.84-1.84,2.84-3.6h-2.72v-5.96h5.96v5.24c0,2.08-.68,3.56-1.72,4.64-1.12,1.16-2.76,1.84-4.36,2v-2.32Z" />
        <path className="logo-color" d="m242.33,59.96h7.12c.32,2.88,2.2,4.08,5.6,4.08,2.32,0,4.6-.84,4.6-3,0-2.28-2.32-2.76-6.24-3.72-4.96-1.16-10.2-2.68-10.2-8.68s4.96-8.8,11.36-8.8,11,2.8,11.28,8.8h-6.92c-.28-2.2-1.96-3.44-4.6-3.44-2.48,0-4.12,1.08-4.12,2.68,0,2,2.2,2.48,6.48,3.44,5.32,1.24,10.12,2.88,10.12,9s-5.12,9.32-11.72,9.32c-7.6,0-12.6-3.36-12.76-9.68Z" />
        <path className="logo-color" d="m277.89,40.37h12.88c3.48,0,6.04.96,7.8,2.64,1.84,1.72,2.88,4.16,2.88,7.04,0,5.48-3.8,9.36-9.68,9.36h-6.68v9.56h-7.2v-28.6Zm12.44,13.44c2.56,0,4-1.52,4-3.8s-1.48-3.68-4-3.68h-5.24v7.48h5.24Z" />
        <path className="logo-color" d="m304.2,40.37h7.08v22.6h12.84v6h-19.92v-28.6Z" />
        <path className="logo-color" d="m336.27,40.37h7.28l10.32,28.6h-7.44l-1.68-5.16h-10l-1.64,5.16h-7.12l10.28-28.6Zm6.84,18.36l-2.16-6.92c-.48-1.48-1.12-4.04-1.12-4.04h-.08s-.68,2.56-1.16,4.04l-2.2,6.92h6.72Z" />
        <path className="logo-color" d="m360.56,58.4l-9.68-18.04h7.72l3.68,7.4c.96,1.96,1.88,4.36,1.88,4.36h.08s1-2.4,1.96-4.36l3.64-7.4h7.48l-9.68,18.04v10.56h-7.08v-10.56Z" />
      </g>
    </svg>
  );
}

export function getDarkTextLogo() {
  return (
    <svg className="logo" data-name="text-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 420 220">
      <title>Let's Play Bingo Logo</title>
      <g id="Bingo">
        <path
          className="logo-dark"
          d="m41.86,84.03c.97-.04,1.81-.03,2.5.04l2.06.2c.14,1.4-.48,2.73-1.85,3.99-1.37,1.26-3.01,2.35-4.9,3.27-1.9.93-3.78,1.68-5.64,2.26-1.86.58-3.28.9-4.26.94-1.26.16-2.36-.05-3.27-.63-.92-.57-1.68-1.31-2.28-2.2-1.88-3.52-2.49-7.25-1.82-11.22.66-3.96,2.05-7.85,4.16-11.68,2.11-3.82,4.68-7.42,7.72-10.81,3.04-3.38,5.98-6.19,8.84-8.41,7.2-5.41,14.74-9.78,22.63-13.1,7.89-3.32,16.15-5.75,24.77-7.27,3.94-.58,8.27-1.1,12.96-1.55,4.7-.44,9.32-.55,13.89-.31,4.56.24,8.84.86,12.84,1.88,4,1.02,7.41,2.67,10.25,4.96,2.83,2.29,4.83,5.3,5.99,9.02,1.16,3.73,1.13,8.38-.11,13.95-1.56,4.57-3.55,8.58-5.97,12.02-2.42,3.45-5.09,6.55-7.99,9.32-2.91,2.77-5.9,5.29-8.97,7.56-3.07,2.27-5.96,4.45-8.66,6.55,4.18-.15,8.32.47,12.42,1.84,4.1,1.37,7.81,3.26,11.11,5.67,3.31,2.41,6.11,5.32,8.42,8.74,2.3,3.42,3.75,7.1,4.34,11.05.73,8.13-.57,15.81-3.92,23.05-3.35,7.25-7.86,13.96-13.53,20.14-5.67,6.18-12.01,11.74-19.02,16.68-7.01,4.94-13.81,9.17-20.41,12.69-5.44,3.08-11.95,6.12-19.54,9.13-7.58,3.01-15.28,5.35-23.08,7.02-7.8,1.67-15.21,2.44-22.2,2.31-7-.13-12.7-1.86-17.1-5.21-2.45-1.91-4.18-4.13-5.18-6.65-1-2.53-1.38-5.07-1.13-7.61s1.08-5.07,2.51-7.57c1.42-2.5,3.28-4.82,5.58-6.96,4.76-4.68,9.94-8.68,15.55-12.02,5.6-3.34,11.45-6.31,17.54-8.91,5.22-2.27,10.57-4.07,16.05-5.41,5.48-1.34,10.97-2.5,16.49-3.49,4.8-.78,9.42-1.23,13.88-1.35.41.04.83.05,1.25.02.42-.03.76.04,1.02.2.69.07,1.08.31,1.18.74.1.43-.09.9-.55,1.4-.3.25-.71.56-1.23.92-.52.37-1,.67-1.44.9-5.07,2.14-10.15,3.98-15.22,5.49-5.08,1.52-10.15,3.35-15.22,5.49-4.34,1.8-8.69,3.67-13.05,5.6-4.36,1.94-8.59,4.06-12.7,6.36-4.12,2.31-7.97,4.85-11.58,7.62-3.6,2.77-6.82,5.82-9.64,9.16-.82,1.31-1.43,2.53-1.82,3.68-.39,1.14.13,2.2,1.56,3.17,3.3,1.71,7.73,2.28,13.27,1.71,5.54-.57,11.42-1.77,17.64-3.59,6.22-1.82,12.31-4.08,18.26-6.76,5.95-2.68,10.94-5.36,14.95-8.02,6.74-3.65,13.26-8.5,19.55-14.55,6.28-6.05,11.42-12.46,15.42-19.22,3.99-6.76,6.34-13.41,7.05-19.94.71-6.53-1.22-12.03-5.78-16.5-1.24-1.51-2.86-2.68-4.87-3.5s-4.17-1.41-6.49-1.78c-2.33-.36-4.68-.49-7.05-.38-2.37.12-4.41.37-6.12.76-3.28.79-6.54,1.9-9.8,3.31-3.26,1.42-6.39,3.16-9.36,5.23.31-3.16,1.69-6.29,4.15-9.39,2.45-3.09,5.34-6.04,8.67-8.84,3.33-2.8,6.74-5.42,10.25-7.85,3.5-2.44,6.38-4.59,8.65-6.45,2.9-2.63,5.67-5.83,8.33-9.6,2.66-3.77,4.59-7.57,5.8-11.41,1.21-3.84,1.46-7.46.75-10.86-.71-3.4-2.91-6.19-6.58-8.35-2.77-1.52-5.87-2.45-9.31-2.78s-6.95-.26-10.54.22c-3.59.48-7.14,1.25-10.64,2.3-3.51,1.05-6.7,2.13-9.59,3.23-4.18,1.54-8.24,3.67-12.19,6.41-3.95,2.74-7.38,5.91-10.3,9.52-2.92,3.6-5.14,7.49-6.66,11.64-1.52,4.16-2.07,8.41-1.66,12.75Zm1.44,68.47c-3,.82-6.59,2.45-10.79,4.88-4.2,2.44-8.46,5.18-12.78,8.23.48-3.56,1.68-8.38,3.6-14.44,1.91-6.06,4.09-12.38,6.54-18.95,2.44-6.56,4.9-12.85,7.36-18.86,2.46-6.01,4.49-10.81,6.09-14.41,4.11-7.93,8.25-15.55,12.44-22.85,4.18-7.3,9.49-13.97,15.91-20.01,3.97-3.64,8.75-5.74,14.37-6.31,1.83-.24,3.55-.07,5.16.5,1.76.45,3.1,1.34,4.01,2.68.91,1.34.99,2.95.26,4.82-.07.69-.27,1.33-.6,1.92-.34.59-.68,1.29-1.04,2.09-.66,1.05-1.34,1.99-2.05,2.82-.71.84-1.38,1.71-2.03,2.61-5.69,7.08-10.83,14.15-15.4,21.2-4.58,7.05-8.71,14.15-12.39,21.29-3.69,7.14-7.05,14.28-10.11,21.41-3.06,7.13-5.91,14.25-8.54,21.35Z"
        />
        <g>
          <path
            className="logo-dark"
            d="m190.31,143.73c0-2-2-1-2-1-2,1-2.16,2.4-4.84,5.16-2.69,2.76-5.1,4.39-7.26,4.88-1.84.47-3.08.1-3.71-1.11-.63-1.2-.91-2.62-.85-4.24.56-4.29,1.69-8.65,3.38-13.09,1.7-4.44,3.37-9.15,5.02-14.14.66-1.97.91-3.86.75-5.67,0-1.11-.27-2.09-.81-2.95-.55-.86-1.19-1.56-1.91-2.1-.73-.54-1.58-.94-2.54-1.21-.97-.27-1.86-.36-2.7-.29-3.84.34-6.87,2.13-9.1,5.35-.46.75-.89,1.52-1.29,2.31-.4.79-.77,1.61-1.12,2.44-1.94,5.22-3.68,10.34-5.22,15.37-1.54,5.03-2.11,10.4-1.7,16.12.16,2.91.9,5.44,2.25,7.59,1.34,2.15,3.08,3.78,5.22,4.91,2.14,1.12,4.56,1.61,7.28,1.47,2.71-.14,5.5-1.05,8.39-2.72,2.67-1.75,5.69-3.79,8.1-6.93q1.66-2.16,2.66-4.16c2-4,2-6,2-6h0Z"
          />
          <path className="logo-dark" d="m165.76,101.3c1.4,1.59,3.06,2.37,5,2.35,1.93-.02,3.82-.57,5.66-1.64,1.84-1.07,3.47-2.5,4.88-4.29,1.41-1.79,2.29-3.61,2.65-5.46.52-3.58-.24-5.88-2.27-6.91-2.03-1.03-4.84-1.18-8.44-.46-2.15.49-3.98,1.39-5.49,2.68-1.51,1.3-2.61,2.73-3.31,4.3-.7,1.58-.97,3.19-.82,4.84.15,1.65.86,3.18,2.14,4.58Z" />
        </g>
        <path
          className="logo-dark"
          d="m243.48,96.27c2.88,1.76,4.75,3.91,5.6,6.46.86,2.55,1.05,5.38.6,8.5-.46,3.12-1.31,6.47-2.56,10.06s-2.55,7.21-3.9,10.86c-1.35,3.65-2.54,7.29-3.58,10.91-1.04,3.63-1.47,7.04-1.28,10.25.28,1.99,1.35,3.08,3.2,3.27,1.85.19,3.66-.3,5.44-1.47.8-.48,1.53-.99,2.22-1.56.68-.57,1.36-1.13,2.05-1.7.4-.24.74-.55,1.02-.92.28-.38.67-.64,1.19-.79.53.05.71.34.55.86-.36.74-.65,1.47-.9,2.2-.25.73-.61,1.42-1.07,2.06-.46.75-.95,1.39-1.47,1.95-.53.55-1.03,1.1-1.5,1.65-1.54,1.55-3.57,2.79-6.11,3.72-2.53.93-4.75,1.28-6.66,1.05-5.26-.24-8.88-1.83-10.87-4.78-1.99-2.95-2.97-6.6-2.94-10.93.03-4.34.71-9.01,2.05-14.03,1.33-5.01,2.74-9.7,4.24-14.07s2.68-8.06,3.57-11.06c.88-3,.89-4.67.03-5-1.98.08-4.1,1.02-6.34,2.84-2.25,1.82-4.41,3.98-6.49,6.48-2.08,2.51-3.93,5.19-5.56,8.06s-2.81,5.45-3.55,7.73c-1.78,3.59-3.26,6.92-4.45,10.01-1.19,3.08-2.41,6.45-3.65,10.09-.34.84-.75,1.56-1.22,2.15-.47.6-1.11,1.03-1.92,1.31-1.95.48-3.68.43-5.19-.14-1.76-.95-3-2.53-3.72-4.74-.72-2.21-1.14-4.56-1.26-7.08-.12-2.51-.07-4.96.13-7.35.21-2.39.4-4.3.59-5.73.45-3.17,1.03-6.37,1.73-9.61.71-3.24,1.48-6.34,2.31-9.29.84-2.95,1.8-5.66,2.88-8.13,1.09-2.47,2.24-4.51,3.46-6.13,1.45-2.55,3.39-4.24,5.83-5.06,1.83-.57,3.45-.63,4.85-.21,1.4.43,2.43,1.47,3.11,3.13.83,2.25,1.02,4.37.57,6.38s-1,4.05-1.65,6.12c-.42,1.15-.8,2.17-1.14,3.05-.34.89-.68,1.78-1.02,2.66-.34.89-.66,1.77-.94,2.66-.29.88-.59,1.94-.89,3.18,2.16-3.92,4.72-7.51,7.67-10.75,2.95-3.24,6.02-5.83,9.2-7.78,3.18-1.95,6.32-3.08,9.43-3.41,3.11-.33,5.89.36,8.35,2.05Z"
        />
        <path
          className="logo-dark"
          d="m286.08,164.93c.05.6-.11,1.45-.48,2.54s-.83,2.19-1.35,3.3c-.53,1.11-1.07,2.11-1.61,3.02-.55.9-.96,1.52-1.25,1.85-3.45-1.11-6.99-1.52-10.63-1.25-3.64.27-6.96,1.15-9.97,2.63-3.01,1.48-5.5,3.49-7.47,6.04-1.97,2.54-2.96,5.58-2.96,9.12.76,2.66,2.28,4.46,4.57,5.42,2.28.95,4.76,1.11,7.42.47,4.89-1.55,9.05-3.81,12.5-6.79,3.45-2.98,6.67-7.03,9.67-12.14,1.6-3.17,3.05-6.58,4.35-10.22,1.3-3.65,2.41-7.35,3.33-11.12.92-3.77,1.74-7.52,2.45-11.26.71-3.74,1.33-7.36,1.86-10.83-2.01,2.1-4.08,4.17-6.2,6.23-2.12,2.06-4.28,3.99-6.47,5.8-2.19,1.81-4.44,3.37-6.73,4.69-2.29,1.32-4.61,2.28-6.96,2.89-1.73.56-3.51.87-5.33.93-1.83.06-3.54-.11-5.15-.52-1.61-.41-3.05-1.09-4.34-2.03-1.29-.94-2.21-2.15-2.76-3.61-1.08-2.73-1.65-5.6-1.71-8.62-.06-3.02.25-6.02.92-9.01.68-2.98,1.67-5.95,2.98-8.89,1.31-2.94,2.74-5.69,4.29-8.25,3.87-5.89,8.76-11.15,14.69-15.76,5.92-4.61,12.1-7.76,18.55-9.45,2.35-.61,4.61-.86,6.76-.75,2.15.11,4.18.79,6.07,2.03.55.36,1.1.66,1.65.91.54.25,1.09.56,1.65.91,3.25,2.43,4.76,5.78,4.51,10.04-.12,1.02-.26,2.04-.43,3.07-.17,1.02-.52,2.07-1.06,3.12-.72,1.27-1.38,1.84-1.97,1.69-.59-.15-1.03-.67-1.31-1.55-.44-1.37-.79-2.68-1.06-3.92-.27-1.24-.86-2.32-1.78-3.25-.62-1.05-1.4-1.62-2.35-1.68-.95-.07-1.88.04-2.8.33-2.94,1.07-5.87,2.59-8.78,4.57-2.91,1.98-5.42,4.07-7.53,6.27-2,2.2-4,4.7-6,7.5-2,2.8-3.71,5.65-5.12,8.55-1.42,2.9-2.45,5.74-3.09,8.52-.64,2.78-.61,5.25.11,7.4.63,1.26,1.65,1.74,3.03,1.47s2.83-.88,4.31-1.82c1.49-.94,2.91-2,4.27-3.18,1.36-1.18,2.33-2.05,2.91-2.6,3.2-2.81,6.4-5.72,9.58-8.72,3.18-3.01,6.06-5.38,8.63-7.12,1.07-.9,2.22-1.46,3.46-1.67,1.24-.21,2.49-.27,3.75-.18,1.58.16,2.44.99,2.58,2.49.46,1.67.76,3.26.9,4.76.15,2.91,0,5.9-.46,8.97-.46,3.07-1.03,6.09-1.7,9.08-.97,4.42-1.95,8.62-2.96,12.6-1.01,3.97-2.22,7.99-3.63,12.05,8.53,4.08,16.1,6.51,22.71,7.28,6.61.77,12.05.41,16.31-1.08,2.92-1.27,5.16-2.86,6.72-4.76,1.56-1.9,2.36-4.32,2.41-7.25,1.15,1.21,1.77,2.59,1.86,4.15.08,1.56-.22,3.12-.92,4.7-.7,1.58-1.69,3.1-2.97,4.58-1.28,1.48-2.61,2.68-3.99,3.61-2.67,1.75-5.73,2.98-9.17,3.69-3.44.71-7.11.99-10.99.83-3.89-.16-7.94-.71-12.16-1.64-4.22-.94-8.49-2.22-12.82-3.85-4.2,6.93-9.16,12.92-14.88,17.97-5.73,5.05-12.6,8.39-20.62,10.01-3.2.49-6.21.48-9.03-.03-2.82-.51-5.29-1.5-7.41-2.97-2.12-1.48-3.75-3.37-4.9-5.69-1.15-2.32-1.65-5.05-1.52-8.19.4-4.88,2.08-9.19,5.05-12.93,2.96-3.75,6.54-6.79,10.73-9.13,4.19-2.34,8.7-3.9,13.55-4.69,4.85-.79,9.41-.66,13.69.37Z"
        />
        <path
          className="logo-dark"
          d="m317.84,126.44c1.47-6.33,4.4-12.41,8.8-18.25,2.15-2.76,4.58-5.24,7.3-7.43,2.72-2.19,5.68-3.81,8.87-4.86,1.9-.6,3.64-.78,5.22-.54.71.07,1.32.31,1.83.72.93.6.74,1.26-.55,1.97-1.94,1.07-3.77,2.89-5.48,5.47-1.71,2.58-3.17,5.51-4.37,8.78-1.2,3.27-2.11,6.64-2.72,10.11-.61,3.47-.85,6.68-.71,9.62.13,2.94.73,5.36,1.79,7.25,1.06,1.89,2.62,2.86,4.68,2.91,1.87-.41,3.7-1.52,5.51-3.34,1.8-1.82,3.45-4.06,4.96-6.74,1.5-2.68,2.82-5.66,3.96-8.94,1.14-3.29,2.03-6.56,2.67-9.82.64-3.26,1.02-6.39,1.15-9.39.12-3-.1-5.6-.66-7.78-.5-1.49-1.32-2.65-2.45-3.49-1.13-.83-2.39-1.41-3.77-1.74-1.38-.32-2.84-.42-4.36-.28-1.53.14-2.99.45-4.37.93-1.16.48-2.35,1.03-3.56,1.67-1.21.63-2.37,1.27-3.47,1.9-.19.08-.4.19-.63.33-.23.14-.45.21-.68.21-.39.16-.61.16-.68,0-.13-.3-.16-.52-.09-.63.46-.81.98-1.48,1.56-1.98,2.08-2.11,4.37-3.97,6.88-5.58,2.51-1.61,5.23-2.84,8.16-3.7,4.7-.95,8.61-.54,11.71,1.22,3.1,1.76,5.46,4.19,7.08,7.29,1.54,3.75,2.24,7.78,2.09,12.09-.15,4.31-1,8.64-2.55,12.99-1.55,4.35-3.71,8.6-6.48,12.73-2.77,4.14-6,7.91-9.7,11.31-2.56,2.31-5.39,4.17-8.49,5.57-3.1,1.41-6.17,2.34-9.22,2.79-3.04.45-5.88.36-8.51-.25-2.63-.62-4.71-1.82-6.24-3.6-2.52-2.81-4.09-5.88-4.72-9.19-.63-3.32-.54-6.75.26-10.31Z"
        />
      </g>
      <path
        id="_"
        data-name="!"
        className="logo-dark"
        d="m388.36,124.46c1.51-.44,2.83-.52,3.95-.24,1.12.28,2.11.76,2.97,1.43,1.85,1.53,3.13,3.53,3.86,6.03,1.24,4.26.53,7.75-2.15,10.45-.89.9-2.07,1.56-3.53,1.99-3.37.98-6.14.79-8.34-.56-2.19-1.35-3.63-3.21-4.32-5.57-.82-2.82-.6-5.49.68-7.99,1.44-2.77,3.74-4.61,6.88-5.53Zm28.59-81.65c2.48-.53,4.61,1.82,3.82,4.24-3.51,10.69-6.69,21.41-9.52,32.15-3.13,11.87-6.24,23.8-9.32,35.79-.34,1.32-1.46,2.31-2.81,2.45-2.15.23-4.29.51-6.4.84-1.36.21-2.73.42-4.1.64-2.22.34-4.12-1.59-3.74-3.8,1.78-10.47,3.49-20.93,5.13-31.39,1.76-11.25,3.64-22.56,5.65-33.92.23-1.3,1.2-2.33,2.48-2.63s2.73-.64,4.29-.99c2.45-.55,4.89-1.12,7.33-1.72,2.44-.59,4.82-1.14,7.12-1.63.02,0,.05,0,.07-.01Z"
      />
      <g>
        <path className="logo-med" d="m160.29,40.37h7.08v22.6h12.84v6h-19.92v-28.6Z" />
        <path className="logo-med" d="m183.02,40.37h21.68v5.84h-14.6v5.12h12.52v5.52h-12.52v6.12h14.84v6h-21.92v-28.6Z" />
        <path className="logo-med" d="m215.6,46.36h-8.32v-6h23.8v6h-8.4v22.6h-7.08v-22.6Z" />
        <path className="logo-med" d="m233.5,49.92c1.56-.48,2.84-1.84,2.84-3.6h-2.72v-5.96h5.96v5.24c0,2.08-.68,3.56-1.72,4.64-1.12,1.16-2.76,1.84-4.36,2v-2.32Z" />
        <path className="logo-med" d="m242.33,59.96h7.12c.32,2.88,2.2,4.08,5.6,4.08,2.32,0,4.6-.84,4.6-3,0-2.28-2.32-2.76-6.24-3.72-4.96-1.16-10.2-2.68-10.2-8.68s4.96-8.8,11.36-8.8,11,2.8,11.28,8.8h-6.92c-.28-2.2-1.96-3.44-4.6-3.44-2.48,0-4.12,1.08-4.12,2.68,0,2,2.2,2.48,6.48,3.44,5.32,1.24,10.12,2.88,10.12,9s-5.12,9.32-11.72,9.32c-7.6,0-12.6-3.36-12.76-9.68Z" />
        <path className="logo-med" d="m277.89,40.37h12.88c3.48,0,6.04.96,7.8,2.64,1.84,1.72,2.88,4.16,2.88,7.04,0,5.48-3.8,9.36-9.68,9.36h-6.68v9.56h-7.2v-28.6Zm12.44,13.44c2.56,0,4-1.52,4-3.8s-1.48-3.68-4-3.68h-5.24v7.48h5.24Z" />
        <path className="logo-med" d="m304.2,40.37h7.08v22.6h12.84v6h-19.92v-28.6Z" />
        <path className="logo-med" d="m336.27,40.37h7.28l10.32,28.6h-7.44l-1.68-5.16h-10l-1.64,5.16h-7.12l10.28-28.6Zm6.84,18.36l-2.16-6.92c-.48-1.48-1.12-4.04-1.12-4.04h-.08s-.68,2.56-1.16,4.04l-2.2,6.92h6.72Z" />
        <path className="logo-med" d="m360.56,58.4l-9.68-18.04h7.72l3.68,7.4c.96,1.96,1.88,4.36,1.88,4.36h.08s1-2.4,1.96-4.36l3.64-7.4h7.48l-9.68,18.04v10.56h-7.08v-10.56Z" />
      </g>
    </svg>
  );
}

export function getLogo() {
  return (
    <svg className="logo" data-name="logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 569 210.5">
      <title>Let's Play Bingo Logo</title>
      <defs>
        <linearGradient id="rainbowGrad" x1="10%" y1="5%" x2="120%" y2="20%">
          <stop className="stop1" offset="0%" />
          <stop className="stop2" offset="16%" />
          <stop className="stop3" offset="33%" />
          <stop className="stop4" offset="50%" />
          <stop className="stop5" offset="67%" />
          <stop className="stop6" offset="84%" />
        </linearGradient>
      </defs>
      <g id="ball_copy_2" data-name="ball copy 2">
        <circle className="logo-color" cx="468" cy="102" r="94" />
        <circle className="logo-white" cx="486.82" cy="92.27" r="56.01" />
        <circle className="logo-stroke" cx="486.82" cy="92.27" r="62.73" />
        <g id="B7">
          <path className="logo-dark" d="m489.5,52.51c8.5,0,12.43,3.55,12.43,8.79,0,3.98-1.87,6.43-5.81,7.82v.1c4.46,1.01,6.67,3.65,6.67,7.97,0,5.81-4.61,9.5-12.48,9.5h-14.16v-34.18h13.34Zm-5.18,13.83h4.32c3.7,0,5.28-1.2,5.28-3.74s-1.54-3.65-4.99-3.65h-4.61v7.39Zm0,13.87h4.51c3.89,0,5.42-1.2,5.42-3.79,0-2.74-1.58-3.98-5.57-3.98h-4.37v7.78Z" />
          <path className="logo-dark" d="m499.77,103.01c-4.8,6.72-8.54,16.08-10.27,25.68h-8.83c2.26-10.71,6.77-20.11,11.14-25.54h-17.23v-6.82h25.2v6.67Z" />
        </g>
      </g>
      <g id="bingo_copy" data-name="bingo copy">
        <g id="Bingo">
          <path
            className="logo-white"
            d="m41.55,76.3c.97-.04,1.81-.03,2.5.04l2.06.2c.14,1.4-.48,2.73-1.85,3.99-1.37,1.26-3.01,2.35-4.9,3.27-1.9.93-3.78,1.68-5.64,2.26-1.86.58-3.28.9-4.26.94-1.26.16-2.36-.05-3.27-.63-.92-.57-1.68-1.31-2.28-2.2-1.88-3.52-2.49-7.25-1.82-11.22.66-3.96,2.05-7.85,4.16-11.68,2.11-3.82,4.68-7.42,7.72-10.81,3.04-3.38,5.98-6.19,8.84-8.41,7.2-5.41,14.74-9.78,22.63-13.1,7.89-3.32,16.15-5.75,24.77-7.27,3.94-.58,8.27-1.1,12.96-1.55,4.7-.44,9.32-.55,13.89-.31,4.56.24,8.84.86,12.84,1.88,4,1.02,7.41,2.67,10.25,4.96,2.83,2.29,4.83,5.3,5.99,9.02,1.16,3.73,1.13,8.38-.11,13.95-1.56,4.57-3.55,8.58-5.97,12.02-2.42,3.45-5.09,6.55-7.99,9.32-2.91,2.77-5.9,5.29-8.97,7.56-3.07,2.27-5.96,4.45-8.66,6.55,4.18-.15,8.32.47,12.42,1.84,4.1,1.37,7.81,3.26,11.11,5.67,3.31,2.41,6.11,5.32,8.42,8.74,2.3,3.42,3.75,7.1,4.34,11.05.73,8.13-.57,15.81-3.92,23.05-3.35,7.25-7.86,13.96-13.53,20.14-5.67,6.18-12.01,11.74-19.02,16.68-7.01,4.94-13.81,9.17-20.41,12.69-5.44,3.08-11.95,6.12-19.54,9.13-7.58,3.01-15.28,5.35-23.08,7.02-7.8,1.67-15.21,2.44-22.2,2.31-7-.13-12.7-1.86-17.1-5.21-2.45-1.91-4.18-4.13-5.18-6.65-1-2.53-1.38-5.07-1.13-7.61s1.08-5.07,2.51-7.57c1.42-2.5,3.28-4.82,5.58-6.96,4.76-4.68,9.94-8.68,15.55-12.02,5.6-3.34,11.45-6.31,17.54-8.91,5.22-2.27,10.57-4.07,16.05-5.41,5.48-1.34,10.97-2.5,16.49-3.49,4.8-.78,9.42-1.23,13.88-1.35.41.04.83.05,1.25.02.42-.03.76.04,1.02.2.69.07,1.08.31,1.18.74.1.43-.09.9-.55,1.4-.3.25-.71.56-1.23.92-.52.37-1,.67-1.44.9-5.07,2.14-10.15,3.98-15.22,5.49-5.08,1.52-10.15,3.35-15.22,5.49-4.34,1.8-8.69,3.67-13.05,5.6-4.36,1.94-8.59,4.06-12.7,6.36-4.12,2.31-7.97,4.85-11.58,7.62-3.6,2.77-6.82,5.82-9.64,9.16-.82,1.31-1.43,2.53-1.82,3.68-.39,1.14.13,2.2,1.56,3.17,3.3,1.71,7.73,2.28,13.27,1.71,5.54-.57,11.42-1.77,17.64-3.59,6.22-1.82,12.31-4.08,18.26-6.76,5.95-2.68,10.94-5.36,14.95-8.02,6.74-3.65,13.26-8.5,19.55-14.55,6.28-6.05,11.42-12.46,15.42-19.22,3.99-6.76,6.34-13.41,7.05-19.94.71-6.53-1.22-12.03-5.78-16.5-1.24-1.51-2.86-2.68-4.87-3.5s-4.17-1.41-6.49-1.78c-2.33-.36-4.68-.49-7.05-.38-2.37.12-4.41.37-6.12.76-3.28.79-6.54,1.9-9.8,3.31-3.26,1.42-6.39,3.16-9.36,5.23.31-3.16,1.69-6.29,4.15-9.39,2.45-3.09,5.34-6.04,8.67-8.84,3.33-2.8,6.74-5.42,10.25-7.85,3.5-2.44,6.38-4.59,8.65-6.45,2.9-2.63,5.67-5.83,8.33-9.6,2.66-3.77,4.59-7.57,5.8-11.41,1.21-3.84,1.46-7.46.75-10.86-.71-3.4-2.91-6.19-6.58-8.35-2.77-1.52-5.87-2.45-9.31-2.78s-6.95-.26-10.54.22c-3.59.48-7.14,1.25-10.64,2.3-3.51,1.05-6.7,2.13-9.59,3.23-4.18,1.54-8.24,3.67-12.19,6.41-3.95,2.74-7.38,5.91-10.3,9.52-2.92,3.6-5.14,7.49-6.66,11.64-1.52,4.16-2.07,8.41-1.66,12.75Zm1.44,68.47c-3,.82-6.59,2.45-10.79,4.88-4.2,2.44-8.46,5.18-12.78,8.23.48-3.56,1.68-8.38,3.6-14.44,1.91-6.06,4.09-12.38,6.54-18.95,2.44-6.56,4.9-12.85,7.36-18.86,2.46-6.01,4.49-10.81,6.09-14.41,4.11-7.93,8.25-15.55,12.44-22.85,4.18-7.3,9.49-13.97,15.91-20.01,3.97-3.64,8.75-5.74,14.37-6.31,1.83-.24,3.55-.07,5.16.5,1.76.45,3.1,1.34,4.01,2.68.91,1.34.99,2.95.26,4.82-.07.69-.27,1.33-.6,1.92-.34.59-.68,1.29-1.04,2.09-.66,1.05-1.34,1.99-2.05,2.82-.71.84-1.38,1.71-2.03,2.61-5.69,7.08-10.83,14.15-15.4,21.2-4.58,7.05-8.71,14.15-12.39,21.29-3.69,7.14-7.05,14.28-10.11,21.41-3.06,7.13-5.91,14.25-8.54,21.35Z"
          />
          <g>
            <path
              className="logo-white"
              d="m190,136c0-2-2-1-2-1-2,1-2.16,2.4-4.84,5.16-2.69,2.76-5.1,4.39-7.26,4.88-1.84.47-3.08.1-3.71-1.11-.63-1.2-.91-2.62-.85-4.24.56-4.29,1.69-8.65,3.38-13.09,1.7-4.44,3.37-9.15,5.02-14.14.66-1.97.91-3.86.75-5.67,0-1.11-.27-2.09-.81-2.95-.55-.86-1.19-1.56-1.91-2.1-.73-.54-1.58-.94-2.54-1.21-.97-.27-1.86-.36-2.7-.29-3.84.34-6.87,2.13-9.1,5.35-.46.75-.89,1.52-1.29,2.31-.4.79-.77,1.61-1.12,2.44-1.94,5.22-3.68,10.34-5.22,15.37-1.54,5.03-2.11,10.4-1.7,16.12.16,2.91.9,5.44,2.25,7.59,1.34,2.15,3.08,3.78,5.22,4.91,2.14,1.12,4.56,1.61,7.28,1.47,2.71-.14,5.5-1.05,8.39-2.72,2.67-1.75,5.69-3.79,8.1-6.93q1.66-2.16,2.66-4.16c2-4,2-6,2-6h0Z"
            />
            <path className="logo-white" d="m165.44,93.57c1.4,1.59,3.06,2.37,5,2.35,1.93-.02,3.82-.57,5.66-1.64,1.84-1.07,3.47-2.5,4.88-4.29,1.41-1.79,2.29-3.61,2.65-5.46.52-3.58-.24-5.88-2.27-6.91-2.03-1.03-4.84-1.18-8.44-.46-2.15.49-3.98,1.39-5.49,2.68-1.51,1.3-2.61,2.73-3.31,4.3-.7,1.58-.97,3.19-.82,4.84.15,1.65.86,3.18,2.14,4.58Z" />
          </g>
          <path
            className="logo-white"
            d="m243.17,88.54c2.88,1.76,4.75,3.91,5.6,6.46.86,2.55,1.05,5.38.6,8.5-.46,3.12-1.31,6.47-2.56,10.06s-2.55,7.21-3.9,10.86c-1.35,3.65-2.54,7.29-3.58,10.91-1.04,3.63-1.47,7.04-1.28,10.25.28,1.99,1.35,3.08,3.2,3.27,1.85.19,3.66-.3,5.44-1.47.8-.48,1.53-.99,2.22-1.56.68-.57,1.36-1.13,2.05-1.7.4-.24.74-.55,1.02-.92.28-.38.67-.64,1.19-.79.53.05.71.34.55.86-.36.74-.65,1.47-.9,2.2-.25.73-.61,1.42-1.07,2.06-.46.75-.95,1.39-1.47,1.95-.53.55-1.03,1.1-1.5,1.65-1.54,1.55-3.57,2.79-6.11,3.72-2.53.93-4.75,1.28-6.66,1.05-5.26-.24-8.88-1.83-10.87-4.78-1.99-2.95-2.97-6.6-2.94-10.93.03-4.34.71-9.01,2.05-14.03,1.33-5.01,2.74-9.7,4.24-14.07s2.68-8.06,3.57-11.06c.88-3,.89-4.67.03-5-1.98.08-4.1,1.02-6.34,2.84-2.25,1.82-4.41,3.98-6.49,6.48-2.08,2.51-3.93,5.19-5.56,8.06s-2.81,5.45-3.55,7.73c-1.78,3.59-3.26,6.92-4.45,10.01-1.19,3.08-2.41,6.45-3.65,10.09-.34.84-.75,1.56-1.22,2.15-.47.6-1.11,1.03-1.92,1.31-1.95.48-3.68.43-5.19-.14-1.76-.95-3-2.53-3.72-4.74-.72-2.21-1.14-4.56-1.26-7.08-.12-2.51-.07-4.96.13-7.35.21-2.39.4-4.3.59-5.73.45-3.17,1.03-6.37,1.73-9.61.71-3.24,1.48-6.34,2.31-9.29.84-2.95,1.8-5.66,2.88-8.13,1.09-2.47,2.24-4.51,3.46-6.13,1.45-2.55,3.39-4.24,5.83-5.06,1.83-.57,3.45-.63,4.85-.21,1.4.43,2.43,1.47,3.11,3.13.83,2.25,1.02,4.37.57,6.38s-1,4.05-1.65,6.12c-.42,1.15-.8,2.17-1.14,3.05-.34.89-.68,1.78-1.02,2.66-.34.89-.66,1.77-.94,2.66-.29.88-.59,1.94-.89,3.18,2.16-3.92,4.72-7.51,7.67-10.75,2.95-3.24,6.02-5.83,9.2-7.78,3.18-1.95,6.32-3.08,9.43-3.41,3.11-.33,5.89.36,8.35,2.05Z"
          />
          <path
            className="logo-white"
            d="m285.76,157.2c.05.6-.11,1.45-.48,2.54s-.83,2.19-1.35,3.3c-.53,1.11-1.07,2.11-1.61,3.02-.55.9-.96,1.52-1.25,1.85-3.45-1.11-6.99-1.52-10.63-1.25-3.64.27-6.96,1.15-9.97,2.63-3.01,1.48-5.5,3.49-7.47,6.04-1.97,2.54-2.96,5.58-2.96,9.12.76,2.66,2.28,4.46,4.57,5.42,2.28.95,4.76,1.11,7.42.47,4.89-1.55,9.05-3.81,12.5-6.79,3.45-2.98,6.67-7.03,9.67-12.14,1.6-3.17,3.05-6.58,4.35-10.22,1.3-3.65,2.41-7.35,3.33-11.12.92-3.77,1.74-7.52,2.45-11.26.71-3.74,1.33-7.36,1.86-10.83-2.01,2.1-4.08,4.17-6.2,6.23-2.12,2.06-4.28,3.99-6.47,5.8-2.19,1.81-4.44,3.37-6.73,4.69-2.29,1.32-4.61,2.28-6.96,2.89-1.73.56-3.51.87-5.33.93-1.83.06-3.54-.11-5.15-.52-1.61-.41-3.05-1.09-4.34-2.03-1.29-.94-2.21-2.15-2.76-3.61-1.08-2.73-1.65-5.6-1.71-8.62-.06-3.02.25-6.02.92-9.01.68-2.98,1.67-5.95,2.98-8.89,1.31-2.94,2.74-5.69,4.29-8.25,3.87-5.89,8.76-11.15,14.69-15.76,5.92-4.61,12.1-7.76,18.55-9.45,2.35-.61,4.61-.86,6.76-.75,2.15.11,4.18.79,6.07,2.03.55.36,1.1.66,1.65.91.54.25,1.09.56,1.65.91,3.25,2.43,4.76,5.78,4.51,10.04-.12,1.02-.26,2.04-.43,3.07-.17,1.02-.52,2.07-1.06,3.12-.72,1.27-1.38,1.84-1.97,1.69-.59-.15-1.03-.67-1.31-1.55-.44-1.37-.79-2.68-1.06-3.92-.27-1.24-.86-2.32-1.78-3.25-.62-1.05-1.4-1.62-2.35-1.68-.95-.07-1.88.04-2.8.33-2.94,1.07-5.87,2.59-8.78,4.57-2.91,1.98-5.42,4.07-7.53,6.27-2,2.2-4,4.7-6,7.5-2,2.8-3.71,5.65-5.12,8.55-1.42,2.9-2.45,5.74-3.09,8.52-.64,2.78-.61,5.25.11,7.4.63,1.26,1.65,1.74,3.03,1.47s2.83-.88,4.31-1.82c1.49-.94,2.91-2,4.27-3.18,1.36-1.18,2.33-2.05,2.91-2.6,3.2-2.81,6.4-5.72,9.58-8.72,3.18-3.01,6.06-5.38,8.63-7.12,1.07-.9,2.22-1.46,3.46-1.67,1.24-.21,2.49-.27,3.75-.18,1.58.16,2.44.99,2.58,2.49.46,1.67.76,3.26.9,4.76.15,2.91,0,5.9-.46,8.97-.46,3.07-1.03,6.09-1.7,9.08-.97,4.42-1.95,8.62-2.96,12.6-1.01,3.97-2.22,7.99-3.63,12.05,8.53,4.08,16.1,6.51,22.71,7.28,6.61.77,12.05.41,16.31-1.08,2.92-1.27,5.16-2.86,6.72-4.76,1.56-1.9,2.36-4.32,2.41-7.25,1.15,1.21,1.77,2.59,1.86,4.15.08,1.56-.22,3.12-.92,4.7-.7,1.58-1.69,3.1-2.97,4.58-1.28,1.48-2.61,2.68-3.99,3.61-2.67,1.75-5.73,2.98-9.17,3.69-3.44.71-7.11.99-10.99.83-3.89-.16-7.94-.71-12.16-1.64-4.22-.94-8.49-2.22-12.82-3.85-4.2,6.93-9.16,12.92-14.88,17.97-5.73,5.05-12.6,8.39-20.62,10.01-3.2.49-6.21.48-9.03-.03-2.82-.51-5.29-1.5-7.41-2.97-2.12-1.48-3.75-3.37-4.9-5.69-1.15-2.32-1.65-5.05-1.52-8.19.4-4.88,2.08-9.19,5.05-12.93,2.96-3.75,6.54-6.79,10.73-9.13,4.19-2.34,8.7-3.9,13.55-4.69,4.85-.79,9.41-.66,13.69.37Z"
          />
          <path
            className="logo-white"
            d="m317.53,118.71c1.47-6.33,4.4-12.41,8.8-18.25,2.15-2.76,4.58-5.24,7.3-7.43,2.72-2.19,5.68-3.81,8.87-4.86,1.9-.6,3.64-.78,5.22-.54.71.07,1.32.31,1.83.72.93.6.74,1.26-.55,1.97-1.94,1.07-3.77,2.89-5.48,5.47-1.71,2.58-3.17,5.51-4.37,8.78-1.2,3.27-2.11,6.64-2.72,10.11-.61,3.47-.85,6.68-.71,9.62.13,2.94.73,5.36,1.79,7.25,1.06,1.89,2.62,2.86,4.68,2.91,1.87-.41,3.7-1.52,5.51-3.34,1.8-1.82,3.45-4.06,4.96-6.74,1.5-2.68,2.82-5.66,3.96-8.94,1.14-3.29,2.03-6.56,2.67-9.82.64-3.26,1.02-6.39,1.15-9.39.12-3-.1-5.6-.66-7.78-.5-1.49-1.32-2.65-2.45-3.49-1.13-.83-2.39-1.41-3.77-1.74-1.38-.32-2.84-.42-4.36-.28-1.53.14-2.99.45-4.37.93-1.16.48-2.35,1.03-3.56,1.67-1.21.63-2.37,1.27-3.47,1.9-.19.08-.4.19-.63.33-.23.14-.45.21-.68.21-.39.16-.61.16-.68,0-.13-.3-.16-.52-.09-.63.46-.81.98-1.48,1.56-1.98,2.08-2.11,4.37-3.97,6.88-5.58,2.51-1.61,5.23-2.84,8.16-3.7,4.7-.95,8.61-.54,11.71,1.22,3.1,1.76,5.46,4.19,7.08,7.29,1.54,3.75,2.24,7.78,2.09,12.09-.15,4.31-1,8.64-2.55,12.99-1.55,4.35-3.71,8.6-6.48,12.73-2.77,4.14-6,7.91-9.7,11.31-2.56,2.31-5.39,4.17-8.49,5.57-3.1,1.41-6.17,2.34-9.22,2.79-3.04.45-5.88.36-8.51-.25-2.63-.62-4.71-1.82-6.24-3.6-2.52-2.81-4.09-5.88-4.72-9.19-.63-3.32-.54-6.75.26-10.31Z"
          />
        </g>
        <path
          id="_"
          data-name="!"
          className="logo-white"
          d="m388.04,116.72c1.51-.44,2.83-.52,3.95-.24,1.12.28,2.11.76,2.97,1.43,1.85,1.53,3.13,3.53,3.86,6.03,1.24,4.26.53,7.75-2.15,10.45-.89.9-2.07,1.56-3.53,1.99-3.37.98-6.14.79-8.34-.56-2.19-1.35-3.63-3.21-4.32-5.57-.82-2.82-.6-5.49.68-7.99,1.44-2.77,3.74-4.61,6.88-5.53Zm28.59-81.65c2.48-.53,4.61,1.82,3.82,4.24-3.51,10.69-6.69,21.41-9.52,32.15-3.13,11.87-6.24,23.8-9.32,35.79-.34,1.32-1.46,2.31-2.81,2.45-2.15.23-4.29.51-6.4.84-1.36.21-2.73.42-4.1.64-2.22.34-4.12-1.59-3.74-3.8,1.78-10.47,3.49-20.93,5.13-31.39,1.76-11.25,3.64-22.56,5.65-33.92.23-1.3,1.2-2.33,2.48-2.63s2.73-.64,4.29-.99c2.45-.55,4.89-1.12,7.33-1.72,2.44-.59,4.82-1.14,7.12-1.63.02,0,.05,0,.07-.01Z"
        />
      </g>
      <g>
        <path className="logo-text-color" d="m159.98,32.63h7.08v22.6h12.84v6h-19.92v-28.6Z" />
        <path className="logo-text-color" d="m182.7,32.63h21.68v5.84h-14.6v5.12h12.52v5.52h-12.52v6.12h14.84v6h-21.92v-28.6Z" />
        <path className="logo-text-color" d="m215.29,38.63h-8.32v-6h23.8v6h-8.4v22.6h-7.08v-22.6Z" />
        <path className="logo-text-color" d="m233.19,42.19c1.56-.48,2.84-1.84,2.84-3.6h-2.72v-5.96h5.96v5.24c0,2.08-.68,3.56-1.72,4.64-1.12,1.16-2.76,1.84-4.36,2v-2.32Z" />
        <path className="logo-text-color" d="m242.01,52.23h7.12c.32,2.88,2.2,4.08,5.6,4.08,2.32,0,4.6-.84,4.6-3,0-2.28-2.32-2.76-6.24-3.72-4.96-1.16-10.2-2.68-10.2-8.68s4.96-8.8,11.36-8.8,11,2.8,11.28,8.8h-6.92c-.28-2.2-1.96-3.44-4.6-3.44-2.48,0-4.12,1.08-4.12,2.68,0,2,2.2,2.48,6.48,3.44,5.32,1.24,10.12,2.88,10.12,9s-5.12,9.32-11.72,9.32c-7.6,0-12.6-3.36-12.76-9.68Z" />
        <path className="logo-text-color" d="m277.57,32.63h12.88c3.48,0,6.04.96,7.8,2.64,1.84,1.72,2.88,4.16,2.88,7.04,0,5.48-3.8,9.36-9.68,9.36h-6.68v9.56h-7.2v-28.6Zm12.44,13.44c2.56,0,4-1.52,4-3.8s-1.48-3.68-4-3.68h-5.24v7.48h5.24Z" />
        <path className="logo-text-color" d="m303.88,32.63h7.08v22.6h12.84v6h-19.92v-28.6Z" />
        <path className="logo-text-color" d="m335.96,32.63h7.28l10.32,28.6h-7.44l-1.68-5.16h-10l-1.64,5.16h-7.12l10.28-28.6Zm6.84,18.36l-2.16-6.92c-.48-1.48-1.12-4.04-1.12-4.04h-.08s-.68,2.56-1.16,4.04l-2.2,6.92h6.72Z" />
        <path className="logo-text-color" d="m360.25,50.67l-9.68-18.04h7.72l3.68,7.4c.96,1.96,1.88,4.36,1.88,4.36h.08s1-2.4,1.96-4.36l3.64-7.4h7.48l-9.68,18.04v10.56h-7.08v-10.56Z" />
      </g>
    </svg>
  );
}

/**
 * Returns the Let's Play Bingo Logo
 *
 * @return  {JSX}  SVG
 */
export function getOldLogo() {
  return (
    <svg className="logo" data-name="logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 270 115">
      <title>Let's Play Bingo Logo</title>
      <title>Let's Play Bingo logo</title>
      <defs>
        <linearGradient id="rainbowGrad" x1="10%" y1="5%" x2="120%" y2="20%">
          <stop className="stop1" offset="0%" />
          <stop className="stop2" offset="16%" />
          <stop className="stop3" offset="33%" />
          <stop className="stop4" offset="50%" />
          <stop className="stop5" offset="67%" />
          <stop className="stop6" offset="84%" />
        </linearGradient>
      </defs>
      <path className="logo-color" d="M82.21,58.73q-.09-10.22-.07-20.36l-1.86.24a95.58,95.58,0,0,1,0-9.9Q87,27.94,93.7,27.3A86.68,86.68,0,0,1,93.48,37l-2.27.25Q91,47.21,91,57.22l4.7-.67q0-3.15.09-6.29,1-.23,1.94-.41t2-.29q1-.12,2-.18t2-.07q-.24,8-.41,16.07Q91.88,67,80.59,69q-.2-2.48-.23-5t.05-5Z" transform="translate(-15.33 -23.04)" />
      <path
        className="logo-color"
        d="M124,61.14A17.31,17.31,0,0,1,121.26,63a14,14,0,0,1-5.12,1.46,9.36,9.36,0,0,1-4.25-.37,8.28,8.28,0,0,1-3.3-2.13,11.49,11.49,0,0,1-2.29-3.6,17.88,17.88,0,0,1-1.19-4.76,26.63,26.63,0,0,1,0-6.43,18.73,18.73,0,0,1,1.75-6,13,13,0,0,1,3.68-4.56,10.35,10.35,0,0,1,5.76-2.07,9.65,9.65,0,0,1,3.72.39,8.13,8.13,0,0,1,2.83,1.57,7.34,7.34,0,0,1,1.86,2.46,8.74,8.74,0,0,1,.8,3,15.44,15.44,0,0,1-.15,3.7,8.59,8.59,0,0,1-1.49,3.61,10.16,10.16,0,0,1-3.54,3A19.07,19.07,0,0,1,114,54.1a3.34,3.34,0,0,0,1,1.6,3.12,3.12,0,0,0,2.38.45A10.21,10.21,0,0,0,121.17,55a15.71,15.71,0,0,0,3.21-2.22Q124.21,57,124,61.14Zm-6.11-18a2.26,2.26,0,0,0-.48-1.24,1.32,1.32,0,0,0-1.22-.45,2.6,2.6,0,0,0-2.14,1.83,12,12,0,0,0-.77,4.51,6.71,6.71,0,0,0,3.6-1.73A3.57,3.57,0,0,0,117.93,43.15Z"
        transform="translate(-15.33 -23.04)"
      />
      <path
        className="logo-color"
        d="M129.82,34.4q.1-1.33.24-2.82t.24-2.87Q135,26.78,139.85,25q-.47,4.5-.92,9l4.1-.14q-.09,2.1-.31,4.22t-.58,4.24l-4,.19q-.35,3.7-.68,7.42-.07.71-.08,1.14t0,.68a1.43,1.43,0,0,0,.35.84,1.26,1.26,0,0,0,1.11.33,5.18,5.18,0,0,0,1.5-.35,7.82,7.82,0,0,0,1.25-.58q-.41,4.1-.8,8.22a20.79,20.79,0,0,1-2.57,1.11,12.28,12.28,0,0,1-3.21.73c-2.12.19-3.72-.22-4.8-1.23a6.77,6.77,0,0,1-1.88-4.58q-.06-.71-.05-1.38t.06-1.5q.39-5.17.82-10.32l-2.12.14a60.92,60.92,0,0,1,.64-8.66Z"
        transform="translate(-15.33 -23.04)"
      />
      <path className="logo-color" d="M144.66,40q1.68-3.21,3.4-6.38l-2.56.06a76,76,0,0,1,1.19-10.57q4.73,0,9.47,0a91.5,91.5,0,0,1-1.35,10.41,8.22,8.22,0,0,0-.67,1q-.63,1-1.4,2.14l-2,3.16Q147.72,39.84,144.66,40Z" transform="translate(-15.33 -23.04)" />
      <path
        className="logo-color"
        d="M154.18,51.36a25.61,25.61,0,0,1,6-.21l0,.34c0,.16,0,.32,0,.47s0,.3,0,.46a1.78,1.78,0,0,0,.64,1.15,2.91,2.91,0,0,0,2.16.56c1.39,0,2.07-.59,2-1.66a1.62,1.62,0,0,0-.24-.84,2.13,2.13,0,0,0-1-.68l-3-1.09a10.17,10.17,0,0,1-2-1,7.56,7.56,0,0,1-1.68-1.52,7.3,7.3,0,0,1-1.17-2,8.06,8.06,0,0,1-.5-2.62,10.8,10.8,0,0,1,.66-4.51,8,8,0,0,1,2-3A7.68,7.68,0,0,1,161,33.52a10.82,10.82,0,0,1,3.2-.48,10.05,10.05,0,0,1,3,.43,6.71,6.71,0,0,1,2,1l.15-.82a26.33,26.33,0,0,1,6.05.1q-.69,4.06-1.36,8.14a31.45,31.45,0,0,1-6.33,0l.07-.43a3.22,3.22,0,0,0,.07-.63,1.33,1.33,0,0,0-.52-1,2.15,2.15,0,0,0-1.44-.43,1.93,1.93,0,0,0-1.4.47,1.54,1.54,0,0,0-.46,1.19,1.84,1.84,0,0,0,.2.7,1.39,1.39,0,0,0,.88.65l3.14,1.14a16.45,16.45,0,0,1,2,1,7.76,7.76,0,0,1,1.75,1.39,6.28,6.28,0,0,1,1.23,2,7.08,7.08,0,0,1,.48,2.63,11,11,0,0,1-.79,4.48A9,9,0,0,1,170.86,58a8.51,8.51,0,0,1-3,1.79,11.46,11.46,0,0,1-3.37.65,11.34,11.34,0,0,1-2.91-.27,5.92,5.92,0,0,1-2.27-.93l-.13.89a24.51,24.51,0,0,1-6.06.31Q153.65,55.89,154.18,51.36Z"
        transform="translate(-15.33 -23.04)"
      />
      <path
        className="logo-color"
        d="M184.16,51q1.54-8.66,3.17-17.23l-1.87-.07A71.11,71.11,0,0,1,187,25.36q7.14.38,14.27.92a12.58,12.58,0,0,1,4.55,1.09,7.89,7.89,0,0,1,2.82,2.19,7.31,7.31,0,0,1,1.39,3,12.94,12.94,0,0,1,.27,3.46,13.73,13.73,0,0,1-1,4.52,11.48,11.48,0,0,1-2.5,3.76,11.31,11.31,0,0,1-3.95,2.55,13.26,13.26,0,0,1-5.38.8l-3.93-.13-.72,3.69,3.63.1q-.26,2.07-.67,4.13t-.94,4.14q-7-.09-13.94,0,.2-2.13.57-4.25t.85-4.24Zm14-11.49a2.59,2.59,0,0,0,1.9-.7,3.16,3.16,0,0,0,.91-2.36,2.07,2.07,0,0,0-.51-1.68,2.37,2.37,0,0,0-1.48-.55l-2.64-.15q-.54,2.65-1.08,5.31Z"
        transform="translate(-15.33 -23.04)"
      />
      <path className="logo-color" d="M211.54,33.43q.3-1.87.71-3.73t1-3.7q5.57.54,11.13,1.18-3.37,12.7-6.55,25.64l1.81.12q-.33,1.84-.75,3.68t-1,3.68q-5.29-.28-10.59-.46,3-13.25,6.11-26.26Z" transform="translate(-15.33 -23.04)" />
      <path
        className="logo-color"
        d="M232.78,61.27l.33-1.84a7.68,7.68,0,0,1-2.6,1.6,7.3,7.3,0,0,1-3,.42,5.87,5.87,0,0,1-3.33-1.12,6.33,6.33,0,0,1-1.87-2.41,9.43,9.43,0,0,1-.77-3.1,21,21,0,0,1,0-3.21,20.73,20.73,0,0,1,1.33-6.12,14.9,14.9,0,0,1,2.86-4.69,11.26,11.26,0,0,1,4.07-2.86,9.82,9.82,0,0,1,4.93-.62,6.75,6.75,0,0,1,3.4,1.17,7.54,7.54,0,0,1,1.63,1.65l1.14-1.54q3.19.41,6.37.85-2.42,7.68-4.77,15.47l1.81.19q-.4,1.75-.89,3.49t-1.16,3.48Q237.49,61.64,232.78,61.27Zm-.89-7a1.88,1.88,0,0,0,1.14-.36,4.25,4.25,0,0,0,1.21-1.28,10,10,0,0,0,1.05-2.26,14.51,14.51,0,0,0,.66-3.27,4,4,0,0,0-.21-1.8,1.24,1.24,0,0,0-1.06-.9,2,2,0,0,0-1.5.47A5.3,5.3,0,0,0,232,46.4a10.2,10.2,0,0,0-.92,2.23,13.29,13.29,0,0,0-.49,2.58,5.17,5.17,0,0,0,.19,2.16A1.27,1.27,0,0,0,231.9,54.29Z"
        transform="translate(-15.33 -23.04)"
      />
      <path
        className="logo-color"
        d="M259.75,48.35A40.43,40.43,0,0,1,262,41.68q3.8.62,7.58,1.29a4.67,4.67,0,0,1,.27,1.06q.09.61.13,1.28a11,11,0,0,1,0,1.32q0,.65-.12,1.17a14.74,14.74,0,0,1-1.56,4.46,37.74,37.74,0,0,1-2.92,4.69,57.06,57.06,0,0,1-3.68,4.56q-2,2.21-3.84,4a31.19,31.19,0,0,1-2.48,2.16,13.18,13.18,0,0,1-2.49,1.54,10.64,10.64,0,0,1-2.7.85,12.12,12.12,0,0,1-3.14.09,12.77,12.77,0,0,1-4.9-1.57q1.13-3.28,2.27-6.53l.51.33a9.29,9.29,0,0,0,.87.47,8.55,8.55,0,0,0,1.07.42,5.17,5.17,0,0,0,1.07.24,4.8,4.8,0,0,0,1.6-.07,4,4,0,0,0,1.27-.56,6.94,6.94,0,0,0,.85-.6,4.54,4.54,0,0,0,.47-.43q-.52-3.93-1.43-7.82t-1.89-7.22L247,46.59q.4-1.74,1-3.46t1.24-3.41q3.87.55,7.73,1.14.25.88.57,2.45t.62,3.41q.3,1.85.56,3.71t.46,3.31q.4-.57.8-1.24a15,15,0,0,0,.72-1.38q.32-.7.56-1.36a10,10,0,0,0,.35-1.14Z"
        transform="translate(-15.33 -23.04)"
      />
      <path
        className="logo-dark"
        d="M23.94,112.32,20.35,91.94l-3.24.5q-.37-1.22-.66-2.4t-.52-2.42q-.23-1.24-.38-2.45t-.23-2.48Q29,79.82,42.78,77.44a24.13,24.13,0,0,1,7.81-.23,15.18,15.18,0,0,1,5.77,2A10.88,10.88,0,0,1,60,82.93a11.49,11.49,0,0,1,1.56,4.71A11.9,11.9,0,0,1,61,93a9.47,9.47,0,0,1-2.77,4A18.49,18.49,0,0,1,61,98.53a10.93,10.93,0,0,1,2.37,2.13,11.11,11.11,0,0,1,1.74,3,15.19,15.19,0,0,1,.93,4.12,16.5,16.5,0,0,1-.47,6.06,11.24,11.24,0,0,1-2.79,4.89,12.81,12.81,0,0,1-5.34,3.15,24.54,24.54,0,0,1-8.17.94q-13.34-.35-26.67-.65-.36-1.25-.65-2.44t-.52-2.43q-.23-1.24-.38-2.44t-.24-2.44Zm18.77-17.9a3.38,3.38,0,0,0,2.42-1.1,2.73,2.73,0,0,0,.58-2.12,3.05,3.05,0,0,0-1-1.84,3.25,3.25,0,0,0-2.74-.6l-5.73.76L37,95Zm3.41,17.23a3.91,3.91,0,0,0,3-1.08,3,3,0,0,0,.73-2.49,3.51,3.51,0,0,0-1.34-2.35,4.59,4.59,0,0,0-3.25-.83l-6.74.39.94,6.54Z"
        transform="translate(-15.33 -23.04)"
      />
      <path className="logo-dark" d="M71.71,111.14,69.42,85.82l-3.27.33q-.29-1.55-.5-3t-.36-3q-.15-1.56-.22-3.06T65,73.94q12.06-1.72,24.17-3.06.25,1.66.42,3.26t.28,3.28q.1,1.68.13,3.29T90,84l-3.28.26L88.49,111l3.18,0q.24,1.68.41,3.28t.27,3.29q.1,1.68.14,3.28t0,3.28l-22.8-.68q-.29-1.56-.5-3.06t-.36-3.05q-.15-1.56-.22-3.05t-.08-3.05Z" transform="translate(-15.33 -23.04)" />
      <path
        className="logo-dark"
        d="M98.76,110.91,97.29,83.48,94,83.71q-.24-1.69-.41-3.3t-.26-3.31q-.1-1.69-.12-3.32t0-3.34q9.58-1,19.18-1.76,6.76,12.7,13.17,26.11L125.32,82l-2.21.09q-.19-1.8-.3-3.52T122.66,75q0-1.8,0-3.53t.14-3.54q10.94-.71,21.89-1.1.15,1.86.22,3.63T145,74.1q0,1.86-.07,3.64t-.21,3.64l-3.28.07.15,44.22-14.5-.49q-6.4-13.84-13.17-27.05l.44,12.78h2.21q.19,1.78.32,3.48t.18,3.48q.06,1.78,0,3.48t-.09,3.48l-20.62-.66q-.23-1.7-.4-3.32t-.26-3.32q-.1-1.69-.12-3.32t0-3.32Z"
        transform="translate(-15.33 -23.04)"
      />
      <path
        className="logo-dark"
        d="M177.1,107.23l-2.85-.08q-.08-1.91-.1-3.65t0-3.56q.06-1.82.18-3.6t.32-3.68q11.22.21,22.42.64,0,1.92,0,3.71t-.12,3.62q-.1,1.83-.26,3.57t-.4,3.65l-2.36-.09-.8,15.63q-.56.31-2.35,1.17a34.31,34.31,0,0,1-4.56,1.71,50,50,0,0,1-6.4,1.48,38.8,38.8,0,0,1-7.91.47,26.68,26.68,0,0,1-11.62-2.8,23.42,23.42,0,0,1-8-6.8,28.81,28.81,0,0,1-4.67-9.94,47.78,47.78,0,0,1-1.51-12.34,44.28,44.28,0,0,1,1.73-12.66,29.71,29.71,0,0,1,5-9.92,23.57,23.57,0,0,1,7.8-6.5,22,22,0,0,1,10.23-2.37,21.7,21.7,0,0,1,6.21.94,15.55,15.55,0,0,1,5.13,2.48l.07-1.83q3.34-.19,6.74-.1,3,.08,6.06.44L194,90q-2,.1-3.94.12t-3.86,0q-2-.06-3.89-.19t-3.81-.34l.11-3.15a5.76,5.76,0,0,0-1.35-4.09,5.26,5.26,0,0,0-4.16-1.72,6.2,6.2,0,0,0-3.74,1.16,9.58,9.58,0,0,0-2.82,3.28,18.45,18.45,0,0,0-1.77,5,33,33,0,0,0-.69,6.34,29.48,29.48,0,0,0,.65,7.47,14.7,14.7,0,0,0,2,4.91,8,8,0,0,0,3,2.72,8.24,8.24,0,0,0,3.47.88,17.1,17.1,0,0,0,2.46-.1,6.52,6.52,0,0,0,1.29-.21Z"
        transform="translate(-15.33 -23.04)"
      />
      <path
        className="logo-o"
        d="M222.77,130a24,24,0,0,1-12-3.18,22.2,22.2,0,0,1-7.5-7.54,30.38,30.38,0,0,1-3.78-10.34,49.69,49.69,0,0,1-.72-11.56,49.14,49.14,0,0,1,2-11.49,29.72,29.72,0,0,1,5.13-10,23.26,23.26,0,0,1,8.84-6.78,26.39,26.39,0,0,1,13.14-1.81,26.68,26.68,0,0,1,12.52,3.91A23.47,23.47,0,0,1,248,79.07a28,28,0,0,1,3.47,10.26,45,45,0,0,1,.18,11.16,47,47,0,0,1-2.54,10.91,31.68,31.68,0,0,1-5.4,9.54,25,25,0,0,1-21,9Zm1.29-15.82q3.93.18,6.55-3.62t3.32-11.34q.69-7.64-1.29-11.72a7,7,0,0,0-6-4.39,7.3,7.3,0,0,0-6.84,3.51q-2.64,3.81-3.19,11.58T218,109.92A6.79,6.79,0,0,0,224.06,114.16Z"
        transform="translate(-15.33 -23.04)"
      />
      <path className="logo-dark" d="M257.15,71.81q9.23,1.17,18.41,2.6-3.18,17.17-6.31,34.48-7.1-.56-14.21-1.06Q256.12,89.88,257.15,71.81Zm13,41.16q-.15,2.22-.35,4.33t-.48,4.24q-.28,2.14-.62,4.23t-.75,4.23l-16.68-.54q.11-2.22.25-4.39t.38-4.39q.25-2.21.59-4.38t.74-4.45Q261.66,112.37,270.12,113Z" transform="translate(-15.33 -23.04)" />
    </svg>
  );
}
